import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../classes/BaseComponent';
import { BaseService } from '../classes/BaseService';
import { Global } from '../classes/Global';
import { StorageHelper } from '../helpers/StorageHelper';
import { Log } from '../helpers/Log';
import { UrlValidator } from '../helpers/UrlValidater';
import { CustomListService } from '../custom-list.service';
import { SearchService } from '../search.service';
import { BrowserNavigationHelper } from '../helpers/BrowserNavigationHelper';
import { ModalComponent } from '../modal/modal.component';
import { ToastService } from '../toast/toast.service';
import { StringHelper } from '../helpers/StringHelper';

@Component({
  selector: 'app-my-lists',
  templateUrl: './my-lists.component.html',
  styleUrls: ['./my-lists.component.scss'],
  providers: [SearchService]
})
export class MyListsComponent extends BaseComponent implements OnInit {

  static SORT_AZ = "myListsSortAZ";
  static SORT_CREATED = "myListsSortCreated";
  static SORT_MODIFIED = "myListsSortModified";
  static SORT_LENGTH = "myListsSortLength";
  static SORT_STORAGE_KEY = "myListsSort";

  @ViewChild("createListModal", {static: false}) createListModal;
  @ViewChild("deleteListConfirmationModal", {static: false}) deleteListConfirmationModal;

  lists: Array<any> = [];
  sortingValues: Array<string> = [
    MyListsComponent.SORT_AZ,
    MyListsComponent.SORT_CREATED,
    MyListsComponent.SORT_MODIFIED,
    MyListsComponent.SORT_LENGTH
  ];
  sortKey = MyListsComponent.SORT_AZ;
  selectedMediaType: string;
  mediaTypesForDisplay: Array<any>;
  mediaTypes: Array<any>;
  isLoading = false;
  hasError = false;
  showLoginRequired = false;
  sessionToken = "";
  itemSearchTerm = "";
  itemSuggestions: Array<any> = [];
  selectedItems: Array<any> = [];
  itemSearchTimeout: any = null;
  isSearchingItems = false;
  tagGroups: Array<any> = [];
  selectedTagIds: Array<any> = [];
  isLoadingTags = false;
  tagsError = false;
  showTags = false;
  draggedItemIndex: number = null;
  touchDragIndex: number = null;
  isSavingList = false;
  createListError = false;
  createListForm = {
    title: "",
    description: "",
    isPublic: false,
    rankingMode: false,
    rankingOrder: "asc",
    priceDropNotification: false,
    fourKNotification: false
  };
  createListInitialState = "";
  activeListMenuId = "";
  editingListId = "";
  pendingEditListId = "";
  listToDelete: any = null;
  publicSections: Array<any> = [];
  selectedPublicSection: any = null;
  selectedPublicBucket: any = null;
  publicCategoryLists: Array<any> = [];
  isLoadingPublicCategory = false;
  publicCategoryError = false;
  publicCategoryHasMore = false;
  publicCategoryLimit = 50;
  publicDiscoveryCategoryLimit = 10;
  publicDiscoveryRequestId = 0;
  publicCategoryRequestId = 0;
  activeListSection = "curated";
  previousPublicDiscoveryScrollY = -1;
  routePublicCategoryId = "";
  routePublicCategorySlug = "";
  routePublicListSection = "";
  restoreListsScrollY = -1;
  restoreListsScrollAttempts = 0;
  restoreListsScrollExpectedTargetUrl = "";
  lastSavedListsScrollY = -1;
  publicDiscoveryCacheTtl = 30 * 60 * 1000;
  publicCategoryCacheTtl = 30 * 60 * 1000;
  isRestoringListsScroll = false;
  restorePublicCategoryScrollY = -1;
  restorePublicCategoryScrollAttempts = 0;
  restorePublicCategoryScrollExpectedTargetUrl = "";
  lastSavedPublicCategoryScrollY = -1;
  isRestoringPublicCategoryScroll = false;

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!event || !event.target) {
      return;
    }

    let target = event.target;
    while (target) {
      if (target.classList && target.classList.contains("list-card-menu")) {
        return;
      }
      target = target.parentElement;
    }

    this.closeListMenu();
  }

  @HostListener('window:scroll', ['$event'])
  onPublicDiscoveryScroll(event) {
    if (window.scrollY === this.previousPublicDiscoveryScrollY) {
      return;
    }

    this.previousPublicDiscoveryScrollY = window.scrollY;

    if (this.isBaseListsViewForScrollRestore()) {
      this.saveListsScrollPosition(false);
      this.saveActivePublicDiscoveryCache();
    }

    if (this.isPublicCategoryViewForScrollRestore() && !this.isRestoringPublicCategoryScroll) {
      this.savePublicCategoryCache(false);
    }

    if (this.isRestoringListsScroll || !this.isPublicSectionActive() || this.selectedPublicBucket) {
      return;
    }

    const documentElement = document.documentElement;
    const scrollHeight = Math.max(
      document.body ? document.body.scrollHeight : 0,
      documentElement ? documentElement.scrollHeight : 0
    );
    const distanceFromBottom = scrollHeight - window.innerHeight - window.scrollY;

    if (distanceFromBottom <= 500) {
      this.loadMoreActivePublicSection();
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customListService: CustomListService,
    private searchService: SearchService,
    private toastService: ToastService
  ) {
    super();
    this.initMediaTypes();
  }

  ngOnInit() {
    if (window.history && window.history.scrollRestoration) {
      window.history.scrollRestoration = "manual";
    }

    UrlValidator.validateWishlistUrl(this.route, this.router);
    this.initMediaTypes();
    this.initPublicSections();
    this.initMyListsSort();
    this.initActiveListSectionFromRoute();
    this.normalizeLegacyListsBaseUrl();
    this.normalizeDiscoverBaseUrl();

    this.sessionToken = StorageHelper.getInstance().get(StorageHelper.keys.sessionToken);
    if (!this.sessionToken) {
      this.showLoginRequired = true;
    }

    this.route.params.subscribe(params => {
      this.routePublicCategoryId = params && params['publicCategoryId'] ? String(params['publicCategoryId']) : "";
      this.routePublicCategorySlug = params && params['publicCategorySlug'] ? String(params['publicCategorySlug']) : "";
      this.routePublicListSection = params && params['publicListRouteSection'] ? String(params['publicListRouteSection']) : "";
      this.initActiveListSectionFromRoute();
      this.normalizeLegacyPublicCategoryUrl();
      if (this.routePublicCategoryId) {
        this.activatePublicCategoryFromRoute();
      }
      else if (this.selectedPublicBucket) {
        this.closePublicCategory(false, true);
      }
    });

    this.route.queryParams.subscribe(params => {
      this.pendingEditListId = params && params['editList'] ? String(params['editList']) : "";
      if (this.normalizeLegacyListSectionQuery(params)) {
        return;
      }
      this.initActiveListSectionFromParams(params);
      this.initPublicCategoryFromParams(params);
      this.openPendingEditList();
    });

    this.loadAllLists();
    this.prepareListsScrollRestore();
  }

  initMediaTypes() {
    this.mediaTypesForDisplay = Global.mediaTypesForSelector[Global.getStore()];
    this.mediaTypes = Global.supportedMediaTypes[Global.getStore()];

    if (this.mediaTypes && this.mediaTypes instanceof Array && this.mediaTypes.length > 0) {
      if (this.mediaTypes.indexOf(Global.getMediaType()) === -1) {
        const fallbackMediaType = this.getFallbackMediaTypeForCurrentStore();
        StorageHelper.getInstance().save(StorageHelper.keys.mediaType, fallbackMediaType);
        this.selectedMediaType = Global.getMediaTypeForSelector(fallbackMediaType);
      }
    }

    if (this.selectedMediaType == null) {
      this.selectedMediaType = Global.getMediaTypeForSelector();
    }
  }

  getFallbackMediaTypeForCurrentStore(): string {
    if (this.mediaTypes && this.mediaTypes.indexOf(Global.BUYMOVIES) !== -1) {
      return Global.BUYMOVIES;
    }
    if (this.mediaTypes && this.mediaTypes.indexOf(Global.MOVIES) !== -1) {
      return Global.MOVIES;
    }
    return this.mediaTypes && this.mediaTypes.length > 0 ? this.mediaTypes[0] : Global.BUYMOVIES;
  }

  loadLists() {
    if (!this.sessionToken) {
      this.lists = [];
      this.isLoading = false;
      this.hasError = false;
      this.showLoginRequired = true;
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.lists = [];

    this.customListService.getCustomLists(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken
    ).subscribe(res => {
      if (res && res["message"] === BaseService.COULDNT_LOAD_USER) {
        Global.clearSaveUserInfos();
        this.showLoginRequired = true;
      }
      else {
        this.lists = this.sortLists(this.normalizeListsResponse(res));
        this.openPendingEditList();
      }
      this.isLoading = false;
    }, err => {
      Log.getInstance().d(err);
      this.hasError = true;
      this.isLoading = false;
    });
  }

  loadAllLists() {
    if (this.isMyListsActive()) {
      if (this.sessionToken) {
        this.showLoginRequired = false;
        this.loadLists();
      }
      else {
        this.showLoginRequired = true;
        this.lists = [];
        this.isLoading = false;
        this.hasError = false;
      }
      return;
    }

    this.showLoginRequired = false;
    this.lists = [];
    this.isLoading = false;
    this.hasError = false;
    this.loadPublicDiscovery();
  }

  initPublicSections() {
    this.publicSections = [
      {
        key: "curated",
        titleKey: "curatedLists",
        publisher: "cheapcharts",
        buckets: [],
        isLoading: false,
        isLoadingMore: false,
        hasError: false,
        hasLoaded: false,
        hasMore: true,
        categoryLimit: this.publicDiscoveryCategoryLimit,
        categoryOffset: 0,
        categoryMaxResults: 0,
        requestId: 0
      },
      {
        key: "community",
        titleKey: "communityLists",
        publisher: "community",
        buckets: [],
        isLoading: false,
        isLoadingMore: false,
        hasError: false,
        hasLoaded: false,
        hasMore: true,
        categoryLimit: this.publicDiscoveryCategoryLimit,
        categoryOffset: 0,
        categoryMaxResults: 0,
        requestId: 0
      }
    ];
  }

  getListHeaderTitleKey(): string {
    if (this.activeListSection === "community") {
      return "communityLists";
    }
    if (this.activeListSection === "my") {
      return "myListsHeaderTitle";
    }
    return "editorialLists";
  }

  getListHeaderTextKey(): string {
    if (this.activeListSection === "community") {
      return "communityListsHeaderText";
    }
    if (this.activeListSection === "my") {
      return "myListsHeaderText";
    }
    return "curatedListsHeaderText";
  }

  initActiveListSectionFromRoute() {
    const routeSection = this.getListsRouteSectionFromUrl();
    const categorySection = this.normalizeListRouteSegment(this.routePublicListSection);
    const sectionKey = categorySection || this.normalizeListRouteSegment(routeSection);

    if (sectionKey) {
      this.setActiveListSection(sectionKey, false);
    }
  }

  getListsRouteSectionFromUrl(): string {
    const path = this.router.url.split("?")[0].split("#")[0];
    const parts = path.split("/");
    if (parts.length > 5 && this.isDiscoverRouteSegment(parts[4])) {
      return parts[5];
    }
    return parts.length > 4 ? parts[4] : "";
  }

  normalizeListRouteSegment(segment: string): string {
    if (segment === this.getString("editorialListsUrl") || segment === "editorial-lists" ||
      segment === "redaktionelle-listen" || segment === "listes-editoriales" || segment === "listas-editoriales" ||
      segment === "public-lists" || segment === "lists" || segment === "listen") {
      return "curated";
    }
    if (segment === this.getString("communityListsUrl") || segment === "community-lists") {
      return "community";
    }
    if (segment === this.getString("myListsUrl") || segment === "my-lists" || segment === "meine-listen") {
      return "my";
    }
    return "";
  }

  isDiscoverRouteSegment(segment: string): boolean {
    return segment === this.getString("discoverUrl") ||
      segment === "discover" ||
      segment === "entdecken" ||
      segment === "decouvrir" ||
      segment === "descubrir";
  }

  normalizeLegacyListsBaseUrl() {
    const section = this.getListsRouteSectionFromUrl();
    if (section !== "lists" && section !== "listen") {
      return;
    }

    const path = this.router.url.split("?")[0].split("#")[0];
    const parts = path.split("/");
    if (parts.length === 5) {
      this.router.navigateByUrl(this.getPublicListsUrl(), { replaceUrl: true });
    }
  }

  normalizeDiscoverBaseUrl() {
    const path = this.router.url.split("?")[0].split("#")[0];
    const parts = path.split("/");
    if (parts.length === 5 && this.isDiscoverRouteSegment(parts[4])) {
      this.router.navigateByUrl(this.getPublicListsUrl(), { replaceUrl: true });
    }
  }

  normalizeLegacyPublicCategoryUrl() {
    if (!this.routePublicCategoryId || (this.routePublicListSection !== "lists" && this.routePublicListSection !== "listen")) {
      return;
    }

    const url = this.getPublicCategoryUrlById(this.routePublicCategoryId, this.routePublicCategorySlug || this.routePublicCategoryId, "curated");
    this.savePublicCategoryRouteMarker(url, "curated");
    this.router.navigateByUrl(url, { replaceUrl: true });
  }

  normalizeLegacyListSectionQuery(params: any): boolean {
    const view = params && params['view'] ? String(params['view']) : "";
    const sectionKey = this.normalizeListSection(view);
    if (!sectionKey) {
      return false;
    }

    this.setActiveListSection(sectionKey, false);
    this.router.navigateByUrl(this.getListSectionUrl(sectionKey), { replaceUrl: true });
    return true;
  }

  initActiveListSectionFromParams(params: any) {
    const view = params && params['view'] ? String(params['view']) : "";
    const sectionKey = this.normalizeListSection(view);

    if (sectionKey) {
      this.setActiveListSection(sectionKey, false);
    }
    else if (this.pendingEditListId) {
      this.setActiveListSection("my", false);
    }
  }

  initPublicCategoryFromParams(params: any) {
    const categoryId = params && (params['category'] || params['categoryId'] || params['tagId']) ?
      String(params['category'] || params['categoryId'] || params['tagId']) : "";

    if (!categoryId || this.routePublicCategoryId) {
      return;
    }

    const bucket = this.findPublicBucketById(categoryId);
    const slug = bucket ? this.getSlug(bucket.title) : this.getSlug(params['categorySlug'] || params['tag'] || categoryId);
    const url = this.getPublicCategoryUrlById(categoryId, slug);
    this.savePublicCategoryRouteMarker(url, this.activeListSection);
    this.router.navigateByUrl(url, { replaceUrl: true });
  }

  normalizeListSection(sectionKey: string): string {
    if (sectionKey === "curated" || sectionKey === "editorial") {
      return "curated";
    }
    if (sectionKey === "community") {
      return "community";
    }
    if (sectionKey === "my") {
      return "my";
    }
    return "";
  }

  selectListSection(sectionKey: string) {
    const normalizedSectionKey = this.normalizeListSection(sectionKey);
    if (!normalizedSectionKey) {
      return;
    }

    this.setActiveListSection(normalizedSectionKey, true);
  }

  setActiveListSection(sectionKey: string, updateUrl: boolean) {
    if (this.activeListSection !== sectionKey) {
      this.closePublicCategory();
      this.activeListSection = sectionKey;
    }

    if (updateUrl) {
      this.router.navigateByUrl(this.getListSectionUrl(sectionKey));
    }

    this.ensureActivePublicSectionLoaded();
  }

  isActiveListSection(sectionKey: string): boolean {
    return this.activeListSection === sectionKey;
  }

  isMyListsActive(): boolean {
    return this.activeListSection === "my";
  }

  isPublicSectionActive(): boolean {
    return this.activeListSection === "curated" || this.activeListSection === "community";
  }

  getActivePublicSection(): any {
    return this.getPublicSectionByKey(this.activeListSection);
  }

  getPublicSectionByKey(sectionKey: string): any {
    if (!this.publicSections) {
      return null;
    }

    for (let i = 0; i < this.publicSections.length; i++) {
      if (this.publicSections[i].key === sectionKey) {
        return this.publicSections[i];
      }
    }
    return null;
  }

  loadPublicDiscovery() {
    if (!this.publicSections || this.publicSections.length === 0) {
      this.initPublicSections();
    }

    if (!this.isPublicSectionActive()) {
      return;
    }

    if (!this.routePublicCategoryId) {
      this.closePublicCategory(false);
    }
    this.publicDiscoveryRequestId++;

    const section = this.getActivePublicSection();
    if (!section) {
      return;
    }

    if (this.restorePublicDiscoveryCache(section)) {
      this.reconcilePublicSections();
      this.syncRoutePublicCategoryWithLoadedBuckets();
      this.restoreListsScrollPositionSoon();
      return;
    }

    this.loadPublicSection(section, false);
  }

  ensureActivePublicSectionLoaded() {
    if (!this.isPublicSectionActive() || this.publicDiscoveryRequestId === 0) {
      return;
    }

    const section = this.getActivePublicSection();
    if (section && !section.hasLoaded && !section.isLoading && !section.isLoadingMore) {
      if (this.restorePublicDiscoveryCache(section)) {
        this.reconcilePublicSections();
        this.syncRoutePublicCategoryWithLoadedBuckets();
        this.restoreListsScrollPositionSoon();
      }
      else {
        this.loadPublicSection(section, false);
      }
    }
  }

  loadMoreActivePublicSection() {
    const section = this.getActivePublicSection();
    if (!section || !section.hasMore || section.isLoading || section.isLoadingMore) {
      return;
    }

    this.loadPublicSection(section, true);
  }

  restorePublicDiscoveryCache(section: any): boolean {
    if (!section || typeof sessionStorage === "undefined") {
      return false;
    }

    try {
      const value = sessionStorage.getItem(this.getPublicDiscoveryCacheStorageKey(section.key));
      if (!value) {
        return false;
      }

      const cached = JSON.parse(value);
      if (!this.isPublicDiscoveryCacheValid(cached, section.key)) {
        sessionStorage.removeItem(this.getPublicDiscoveryCacheStorageKey(section.key));
        return false;
      }

      section.buckets = cached.buckets instanceof Array ? cached.buckets : [];
      section.categoryLimit = this.normalizeNumber(cached.categoryLimit) || this.publicDiscoveryCategoryLimit;
      section.categoryOffset = this.normalizeNumber(cached.categoryOffset);
      section.categoryMaxResults = this.normalizeNumber(cached.categoryMaxResults);
      section.hasMore = cached.hasMore === true;
      section.hasLoaded = true;
      section.isLoading = false;
      section.isLoadingMore = false;
      section.hasError = false;
      return true;
    }
    catch (e) {
      Log.getInstance().d(e);
    }

    return false;
  }

  isPublicDiscoveryCacheValid(cached: any, sectionKey: string): boolean {
    if (!cached) {
      return false;
    }

    const timestamp = Number(cached.timestamp);
    if (isNaN(timestamp) || Date.now() - timestamp > this.publicDiscoveryCacheTtl) {
      return false;
    }

    return cached.country === Global.getCountry() &&
      cached.store === Global.getStore() &&
      cached.mediaType === Global.getMediaType() &&
      cached.section === this.getPublicDiscoveryCacheSectionKey(sectionKey);
  }

  saveActivePublicDiscoveryCache() {
    const section = this.getActivePublicSection();
    if (section) {
      this.savePublicDiscoveryCache(section);
    }
  }

  savePublicDiscoveryCache(section: any) {
    if (!section || typeof sessionStorage === "undefined") {
      return;
    }

    try {
      sessionStorage.setItem(this.getPublicDiscoveryCacheStorageKey(section.key), JSON.stringify({
        timestamp: Date.now(),
        country: Global.getCountry(),
        store: Global.getStore(),
        mediaType: Global.getMediaType(),
        section: this.getPublicDiscoveryCacheSectionKey(section.key),
        scrollY: this.isBaseListsViewForScrollRestore() ? (window.scrollY || 0) : this.lastSavedListsScrollY,
        buckets: section.buckets || [],
        categoryLimit: section.categoryLimit,
        categoryOffset: section.categoryOffset,
        categoryMaxResults: section.categoryMaxResults,
        hasMore: section.hasMore === true,
        hasLoaded: section.hasLoaded === true
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getPublicDiscoveryCacheStorageKey(sectionKey: string): string {
    return "customListsDiscovery:v1:" +
      Global.getCountry() + ":" +
      Global.getStore() + ":" +
      Global.getMediaType() + ":" +
      this.getPublicDiscoveryCacheSectionKey(sectionKey);
  }

  getPublicDiscoveryCacheSectionKey(sectionKey: string): string {
    return sectionKey === "community" ? "community" : "public";
  }

  prepareListNavigation(list: any) {
    const targetUrl = this.getListDetailUrl(list);
    if (this.isPublicCategoryViewForScrollRestore()) {
      this.savePublicCategoryCache(true, targetUrl);
    }
    else {
      this.saveListsScrollPosition(true, targetUrl);
    }
    this.saveListReturnUrl(targetUrl);
  }

  openListDetail(list: any, event: Event) {
    if (this.isListMenuEvent(event)) {
      return;
    }

    const targetUrl = this.getListDetailUrl(list);
    if (!targetUrl) {
      return;
    }

    this.prepareListNavigation(list);

    if (this.shouldUseNativeListLink(event)) {
      return;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.closeListMenu();
    this.router.navigateByUrl(targetUrl);
  }

  isListMenuEvent(event: Event): boolean {
    if (!event || !event.target) {
      return false;
    }

    let target = <any>event.target;
    while (target) {
      if (target.classList && target.classList.contains("list-card-menu")) {
        return true;
      }
      target = target.parentElement;
    }
    return false;
  }

  shouldUseNativeListLink(event: Event): boolean {
    if (!event) {
      return false;
    }

    const mouseEvent = <MouseEvent>event;
    return mouseEvent.button === 1 ||
      mouseEvent.ctrlKey === true ||
      mouseEvent.metaKey === true ||
      mouseEvent.shiftKey === true ||
      mouseEvent.altKey === true;
  }

  restorePublicCategoryCache(section: any, bucket: any): boolean {
    if (!section || !bucket || typeof sessionStorage === "undefined") {
      return false;
    }

    try {
      const value = sessionStorage.getItem(this.getPublicCategoryCacheStorageKey(section.key, String(bucket.id)));
      if (!value) {
        return false;
      }

      const cached = JSON.parse(value);
      if (!this.isPublicCategoryCacheValid(cached, section.key, String(bucket.id))) {
        sessionStorage.removeItem(this.getPublicCategoryCacheStorageKey(section.key, String(bucket.id)));
        return false;
      }

      const cachedBucket = cached.bucket || {};
      this.selectedPublicSection = section;
      this.selectedPublicBucket = {
        id: bucket.id,
        title: bucket.title || cachedBucket.title || this.getTitleFromSlug(this.routePublicCategorySlug) || String(bucket.id),
        lists: bucket.lists instanceof Array ? bucket.lists : [],
        maxResults: this.normalizeNumber(bucket.maxResults || cachedBucket.maxResults || cached.maxResults)
      };
      this.publicCategoryLists = cached.publicCategoryLists instanceof Array ? cached.publicCategoryLists :
        cached.lists instanceof Array ? cached.lists : [];
      this.publicCategoryHasMore = cached.hasMore === true;
      this.publicCategoryError = false;
      this.isLoadingPublicCategory = false;
      if (this.normalizeNumber(cached.limit) > 0) {
        this.publicCategoryLimit = this.normalizeNumber(cached.limit);
      }
      this.lastSavedPublicCategoryScrollY = this.normalizeNumber(cached.scrollY);
      this.preparePublicCategoryScrollRestore(cached);
      return true;
    }
    catch (e) {
      Log.getInstance().d(e);
    }

    return false;
  }

  isPublicCategoryCacheValid(cached: any, sectionKey: string, categoryId: string): boolean {
    if (!cached) {
      return false;
    }

    const timestamp = Number(cached.timestamp);
    if (isNaN(timestamp) || Date.now() - timestamp > this.publicCategoryCacheTtl) {
      return false;
    }

    return cached.country === Global.getCountry() &&
      cached.store === Global.getStore() &&
      cached.mediaType === Global.getMediaType() &&
      cached.section === this.getPublicDiscoveryCacheSectionKey(sectionKey) &&
      String(cached.categoryId) === String(categoryId);
  }

  savePublicCategoryCache(markForRestore?: boolean, targetUrl?: string, forceSave?: boolean) {
    if (typeof sessionStorage === "undefined" || !this.selectedPublicSection || !this.selectedPublicBucket) {
      return;
    }

    const categoryId = this.getCurrentPublicCategoryId();
    if (!categoryId) {
      return;
    }

    try {
      const scrollY = this.isPublicCategoryViewForScrollRestore() ? (window.scrollY || 0) : this.lastSavedPublicCategoryScrollY;
      if (markForRestore !== true && forceSave !== true && Math.abs(scrollY - this.lastSavedPublicCategoryScrollY) < 20) {
        return;
      }

      this.lastSavedPublicCategoryScrollY = scrollY;
      sessionStorage.setItem(this.getPublicCategoryCacheStorageKey(this.selectedPublicSection.key, categoryId), JSON.stringify({
        timestamp: Date.now(),
        country: Global.getCountry(),
        store: Global.getStore(),
        mediaType: Global.getMediaType(),
        section: this.getPublicDiscoveryCacheSectionKey(this.selectedPublicSection.key),
        categoryId: categoryId,
        url: this.getCurrentListsScrollUrl(),
        targetUrl: targetUrl || "",
        scrollY: scrollY,
        publicCategoryLists: this.publicCategoryLists || [],
        lists: this.publicCategoryLists || [],
        hasMore: this.publicCategoryHasMore === true,
        limit: this.publicCategoryLimit,
        offset: this.publicCategoryLists ? this.publicCategoryLists.length : 0,
        isLoading: this.isLoadingPublicCategory === true,
        maxResults: this.selectedPublicBucket ? this.selectedPublicBucket.maxResults : 0,
        bucket: {
          id: categoryId,
          title: this.selectedPublicBucket ? this.selectedPublicBucket.title : "",
          maxResults: this.selectedPublicBucket ? this.selectedPublicBucket.maxResults : 0
        },
        pending: markForRestore === true
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getPublicCategoryCacheStorageKey(sectionKey: string, categoryId: string): string {
    return "customListsCategory:v1:" +
      Global.getCountry() + ":" +
      Global.getStore() + ":" +
      Global.getMediaType() + ":" +
      this.getPublicDiscoveryCacheSectionKey(sectionKey) + ":" +
      categoryId;
  }

  getCurrentPublicCategoryId(): string {
    if (this.selectedPublicBucket && this.selectedPublicBucket.id !== undefined && this.selectedPublicBucket.id !== null) {
      return String(this.selectedPublicBucket.id);
    }
    return this.routePublicCategoryId || "";
  }

  saveListReturnUrl(targetUrl: string) {
    if (typeof sessionStorage === "undefined" || !targetUrl) {
      return;
    }

    try {
      sessionStorage.setItem(this.getListReturnUrlStorageKey(), JSON.stringify({
        targetUrl: targetUrl,
        returnUrl: this.router.url.split("?")[0].split("#")[0],
        timestamp: Date.now()
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getListReturnUrlStorageKey(): string {
    return "customListReturnUrl:" + Global.getCountry() + ":" + Global.getStore() + ":" + Global.getMediaType();
  }

  saveListsScrollPosition(markForRestore?: boolean, targetUrl?: string) {
    if (typeof sessionStorage === "undefined") {
      return;
    }
    if (!this.isBaseListsViewForScrollRestore()) {
      return;
    }

    try {
      const scrollY = window.scrollY || 0;
      if (markForRestore !== true && Math.abs(scrollY - this.lastSavedListsScrollY) < 20) {
        return;
      }

      this.lastSavedListsScrollY = scrollY;
      sessionStorage.setItem(this.getListsScrollStorageKey(), JSON.stringify({
        scrollY: scrollY,
        country: Global.getCountry(),
        store: Global.getStore(),
        mediaType: Global.getMediaType(),
        section: this.activeListSection,
        url: this.getCurrentListsScrollUrl(),
        targetUrl: targetUrl || "",
        timestamp: Date.now(),
        pending: markForRestore === true
      }));
      this.saveActivePublicDiscoveryCache();
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getSavedListsScrollPosition(expectedTargetUrl?: string): number {
    if (typeof sessionStorage === "undefined") {
      return -1;
    }

    try {
      const value = sessionStorage.getItem(this.getListsScrollStorageKey());
      if (value !== null && value !== "") {
        const saved = JSON.parse(value);
        if (this.isSavedListsScrollPositionValid(saved, expectedTargetUrl)) {
          return Number(saved.scrollY);
        }
      }
    }
    catch (e) {
      Log.getInstance().d(e);
    }

    return -1;
  }

  getListsScrollStorageKey(): string {
    return "customListsScroll:v2:" + Global.getCountry() + ":" + Global.getStore() + ":" + Global.getMediaType();
  }

  getCurrentListsScrollUrl(): string {
    return this.router.url.split("#")[0];
  }

  isBaseListsViewForScrollRestore(): boolean {
    return !this.routePublicCategoryId && !this.selectedPublicBucket;
  }

  isPublicCategoryViewForScrollRestore(): boolean {
    return this.isPublicSectionActive() && !!this.selectedPublicBucket;
  }

  isSavedListsScrollPositionValid(saved: any, expectedTargetUrl?: string): boolean {
    if (!saved || saved.pending !== true) {
      return false;
    }

    const scrollY = Number(saved.scrollY);
    if (isNaN(scrollY) || scrollY < 0) {
      return false;
    }

    if (saved.country !== Global.getCountry() ||
      saved.store !== Global.getStore() ||
      saved.mediaType !== Global.getMediaType() ||
      saved.section !== this.activeListSection ||
      this.getListsScrollPathFromUrl(saved.url) !== this.getListsScrollPathFromUrl(this.getCurrentListsScrollUrl())) {
      return false;
    }

    if (expectedTargetUrl &&
      this.getListsScrollPathFromUrl(saved.targetUrl) !== this.getListsScrollPathFromUrl(expectedTargetUrl)) {
      return false;
    }

    const timestamp = Number(saved.timestamp);
    if (isNaN(timestamp) || Date.now() - timestamp > 30 * 60 * 1000) {
      return false;
    }

    return true;
  }

  getListsScrollPathFromUrl(url: any): string {
    if (url === undefined || url === null) {
      return "";
    }
    return String(url).split("?")[0];
  }

  prepareListsScrollRestore(expectedTargetUrl?: string) {
    this.restoreListsScrollExpectedTargetUrl = expectedTargetUrl || "";
    this.restoreListsScrollY = this.getSavedListsScrollPosition(this.restoreListsScrollExpectedTargetUrl);
    this.restoreListsScrollAttempts = 0;
    this.isRestoringListsScroll = this.restoreListsScrollY >= 0;
    this.restoreListsScrollPositionSoon();
  }

  consumeSavedListsScrollPosition(expectedTargetUrl?: string) {
    if (typeof sessionStorage === "undefined") {
      return;
    }

    try {
      const value = sessionStorage.getItem(this.getListsScrollStorageKey());
      if (value !== null && value !== "") {
        const saved = JSON.parse(value);
        if (this.isSavedListsScrollPositionValid(saved, expectedTargetUrl)) {
          saved.pending = false;
          sessionStorage.setItem(this.getListsScrollStorageKey(), JSON.stringify(saved));
        }
      }
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  restoreListsScrollPositionSoon() {
    if (this.restoreListsScrollY < 0 || !this.isBaseListsViewForScrollRestore()) {
      this.isRestoringListsScroll = false;
      return;
    }

    this.restoreListsScrollAttempts++;
    setTimeout(() => {
      if (this.restoreListsScrollY < 0 || !this.isBaseListsViewForScrollRestore()) {
        this.isRestoringListsScroll = false;
        return;
      }

      const documentElement = document.documentElement;
      const scrollHeight = Math.max(
        document.body ? document.body.scrollHeight : 0,
        documentElement ? documentElement.scrollHeight : 0
      );
      const maxScrollY = Math.max(0, scrollHeight - window.innerHeight);
      if (maxScrollY + 30 < this.restoreListsScrollY && this.restoreListsScrollAttempts < 20) {
        this.restoreListsScrollPositionSoon();
        return;
      }

      window.scrollTo(0, this.restoreListsScrollY);
      if (this.restoreListsScrollAttempts < 20 && Math.abs(window.scrollY - this.restoreListsScrollY) > 30) {
        this.restoreListsScrollPositionSoon();
      }
      else {
        if (Math.abs(window.scrollY - this.restoreListsScrollY) <= 30) {
          this.consumeSavedListsScrollPosition(this.restoreListsScrollExpectedTargetUrl);
        }
        this.restoreListsScrollExpectedTargetUrl = "";
        this.restoreListsScrollY = -1;
        setTimeout(() => {
          this.isRestoringListsScroll = false;
        }, 500);
      }
    }, 250);
  }

  preparePublicCategoryScrollRestore(cached: any) {
    this.restorePublicCategoryScrollExpectedTargetUrl = "";
    this.restorePublicCategoryScrollY = -1;
    this.restorePublicCategoryScrollAttempts = 0;
    this.isRestoringPublicCategoryScroll = false;

    if (this.isSavedPublicCategoryScrollPositionValid(cached)) {
      this.restorePublicCategoryScrollExpectedTargetUrl = cached.targetUrl || "";
      this.restorePublicCategoryScrollY = Number(cached.scrollY);
      this.isRestoringPublicCategoryScroll = true;
      this.restorePublicCategoryScrollPositionSoon();
      return;
    }

    if (cached && cached.pending === true) {
      this.consumeSavedPublicCategoryScrollPosition(cached.targetUrl || "");
    }
  }

  isSavedPublicCategoryScrollPositionValid(saved: any): boolean {
    if (!saved || saved.pending !== true) {
      return false;
    }

    const scrollY = Number(saved.scrollY);
    if (isNaN(scrollY) || scrollY < 0) {
      return false;
    }

    if (!this.isPublicCategoryCacheValid(saved, this.activeListSection, this.getCurrentPublicCategoryId()) ||
      this.getListsScrollPathFromUrl(saved.url) !== this.getListsScrollPathFromUrl(this.getCurrentListsScrollUrl())) {
      return false;
    }

    if (!saved.targetUrl) {
      return false;
    }

    return this.isPendingListBackNavigation(saved.targetUrl, saved.url);
  }

  isPendingListBackNavigation(targetUrl: string, returnUrl: string): boolean {
    if (typeof sessionStorage === "undefined" || !targetUrl || !returnUrl) {
      return false;
    }

    try {
      const value = sessionStorage.getItem(this.getListBackNavigationStorageKey());
      if (!value) {
        return false;
      }

      const saved = JSON.parse(value);
      if (!saved || !saved.targetUrl || !saved.returnUrl) {
        return false;
      }

      const timestamp = Number(saved.timestamp);
      if (isNaN(timestamp) || Date.now() - timestamp > 2 * 60 * 1000) {
        return false;
      }

      return this.getListsScrollPathFromUrl(saved.targetUrl) === this.getListsScrollPathFromUrl(targetUrl) &&
        this.getListsScrollPathFromUrl(saved.returnUrl) === this.getListsScrollPathFromUrl(returnUrl) &&
        this.getListsScrollPathFromUrl(saved.returnUrl) === this.getListsScrollPathFromUrl(this.getCurrentListsScrollUrl());
    }
    catch (e) {
      Log.getInstance().d(e);
    }

    return false;
  }

  consumeSavedPublicCategoryScrollPosition(expectedTargetUrl?: string) {
    if (typeof sessionStorage === "undefined" || !this.selectedPublicSection || !this.selectedPublicBucket) {
      return;
    }

    try {
      const categoryId = this.getCurrentPublicCategoryId();
      const key = this.getPublicCategoryCacheStorageKey(this.selectedPublicSection.key, categoryId);
      const value = sessionStorage.getItem(key);
      if (value !== null && value !== "") {
        const saved = JSON.parse(value);
        if (this.isPublicCategoryCacheValid(saved, this.selectedPublicSection.key, categoryId) &&
          saved.pending === true &&
          (!expectedTargetUrl || this.getListsScrollPathFromUrl(saved.targetUrl) === this.getListsScrollPathFromUrl(expectedTargetUrl))) {
          saved.pending = false;
          saved.scrollY = window.scrollY >= 0 ? window.scrollY : (this.restorePublicCategoryScrollY || 0);
          saved.timestamp = Date.now();
          sessionStorage.setItem(key, JSON.stringify(saved));
        }
      }
      this.consumeListBackNavigation(expectedTargetUrl);
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  consumeListBackNavigation(expectedTargetUrl?: string) {
    if (typeof sessionStorage === "undefined") {
      return;
    }

    try {
      const value = sessionStorage.getItem(this.getListBackNavigationStorageKey());
      if (!value) {
        return;
      }

      const saved = JSON.parse(value);
      if (!expectedTargetUrl || this.getListsScrollPathFromUrl(saved.targetUrl) === this.getListsScrollPathFromUrl(expectedTargetUrl)) {
        sessionStorage.removeItem(this.getListBackNavigationStorageKey());
      }
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getListBackNavigationStorageKey(): string {
    return "customListBackNavigation:" + Global.getCountry() + ":" + Global.getStore() + ":" + Global.getMediaType();
  }

  restorePublicCategoryScrollPositionSoon() {
    if (this.restorePublicCategoryScrollY < 0 || !this.isPublicCategoryViewForScrollRestore()) {
      this.isRestoringPublicCategoryScroll = false;
      return;
    }

    this.restorePublicCategoryScrollAttempts++;
    setTimeout(() => {
      if (this.restorePublicCategoryScrollY < 0 || !this.isPublicCategoryViewForScrollRestore()) {
        this.isRestoringPublicCategoryScroll = false;
        return;
      }

      const documentElement = document.documentElement;
      const scrollHeight = Math.max(
        document.body ? document.body.scrollHeight : 0,
        documentElement ? documentElement.scrollHeight : 0
      );
      const maxScrollY = Math.max(0, scrollHeight - window.innerHeight);
      if (maxScrollY + 30 < this.restorePublicCategoryScrollY && this.restorePublicCategoryScrollAttempts < 20) {
        this.restorePublicCategoryScrollPositionSoon();
        return;
      }

      window.scrollTo(0, this.restorePublicCategoryScrollY);
      if (this.restorePublicCategoryScrollAttempts < 20 && Math.abs(window.scrollY - this.restorePublicCategoryScrollY) > 30) {
        this.restorePublicCategoryScrollPositionSoon();
      }
      else {
        this.consumeSavedPublicCategoryScrollPosition(this.restorePublicCategoryScrollExpectedTargetUrl);
        this.savePublicCategoryCache(false);
        this.restorePublicCategoryScrollExpectedTargetUrl = "";
        this.restorePublicCategoryScrollY = -1;
        setTimeout(() => {
          this.isRestoringPublicCategoryScroll = false;
        }, 500);
      }
    }, 250);
  }

  loadPublicSection(section: any, append: boolean) {
    if (!section || section.isLoading || section.isLoadingMore) {
      return;
    }

    const categoryLimit = this.getPublicSectionCategoryLimit(section);
    const categoryOffset = append ? this.normalizeNumber(section.categoryOffset) : 0;
    const discoveryRequestId = this.publicDiscoveryRequestId;
    const sectionRequestId = (section.requestId || 0) + 1;
    section.requestId = sectionRequestId;

    if (!append) {
      section.buckets = [];
      section.categoryOffset = 0;
      section.categoryMaxResults = 0;
      section.hasMore = true;
      section.hasLoaded = false;
    }

    section.isLoading = !append;
    section.isLoadingMore = append;
    section.hasError = false;

    this.customListService.getPublicListsByTag(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      section.publisher,
      10,
      0,
      Global.getStore() === Global.ITUNES ? "-3" : "",
      categoryLimit,
      categoryOffset
    ).subscribe(res => {
      if (discoveryRequestId !== this.publicDiscoveryRequestId || sectionRequestId !== section.requestId) {
        return;
      }
      const oldBucketCount = section.buckets ? section.buckets.length : 0;
      const newBuckets = this.normalizePublicBucketsResponse(res, section.key);
      section.buckets = append ? this.appendUniquePublicBuckets(section.buckets, newBuckets) : newBuckets;
      this.updatePublicSectionCategoryPaging(section, res, categoryOffset, categoryLimit, append, oldBucketCount);
      this.reconcilePublicSections();
      this.syncRoutePublicCategoryWithLoadedBuckets();
      section.hasLoaded = true;
      section.isLoading = false;
      section.isLoadingMore = false;
      this.savePublicDiscoveryCache(section);
      this.restoreListsScrollPositionSoon();
    }, err => {
      if (discoveryRequestId !== this.publicDiscoveryRequestId || sectionRequestId !== section.requestId) {
        return;
      }
      Log.getInstance().d(err);
      section.hasError = true;
      section.isLoading = false;
      section.isLoadingMore = false;
    });
  }

  getPublicSectionCategoryLimit(section: any): number {
    const sectionLimit = section ? this.normalizeNumber(section.categoryLimit) : 0;
    return sectionLimit > 0 ? sectionLimit : this.publicDiscoveryCategoryLimit;
  }

  updatePublicSectionCategoryPaging(section: any, res: any, requestOffset: number, requestLimit: number, append: boolean, oldBucketCount: number) {
    const responseCount = this.getPublicResponseCategoryCount(res);
    const responseLimitValue = this.getPublicMetadataValue(res, "categoryLimit");
    const responseOffsetValue = this.getPublicMetadataValue(res, "categoryOffset");
    const maxResultsValue = this.getPublicMetadataValue(res, "categoryMaxResults");
    const hasMoreValue = this.getPublicMetadataValue(res, "hasMore");

    const responseLimit = this.isValidPublicPagingNumber(responseLimitValue, true) ?
      this.normalizeNumber(responseLimitValue) : requestLimit;
    const responseOffset = this.isValidPublicPagingNumber(responseOffsetValue, false) ?
      this.normalizeNumber(responseOffsetValue) : requestOffset;
    const nextOffset = responseOffset + responseLimit;
    const maxResults = this.normalizeNumber(maxResultsValue);

    section.categoryLimit = requestLimit;
    section.categoryOffset = nextOffset;
    if (maxResults > 0) {
      section.categoryMaxResults = maxResults;
    }

    if (hasMoreValue !== undefined && hasMoreValue !== null && hasMoreValue !== "") {
      section.hasMore = this.isTruthyListValue(hasMoreValue);
      return;
    }

    if (section.categoryMaxResults > 0) {
      section.hasMore = nextOffset < section.categoryMaxResults;
      return;
    }

    if (responseCount === 0 || responseCount < requestLimit) {
      section.hasMore = false;
      return;
    }

    if (!append && requestOffset === 0 && responseCount > requestLimit) {
      section.hasMore = false;
      return;
    }

    section.hasMore = !(append && section.buckets && section.buckets.length === oldBucketCount);
  }

  isValidPublicPagingNumber(value: any, requirePositive: boolean): boolean {
    if (value === undefined || value === null || value === "") {
      return false;
    }
    const numberValue = Number(value);
    return !isNaN(numberValue) && (requirePositive ? numberValue > 0 : numberValue >= 0);
  }

  getPublicResponseCategoryCount(res: any): number {
    const rawBuckets = this.getPublicResultValue(res);
    return rawBuckets instanceof Array ? rawBuckets.length : 0;
  }

  getPublicMetadataValue(res: any, key: string): any {
    const containers = [
      res,
      res && res.results,
      res && res.result,
      res && res.additionalInfo,
      res && res.results && res.results.result,
      res && res.results && res.results.additionalInfo
    ];

    for (let i = 0; i < containers.length; i++) {
      const container = containers[i];
      if (container && !(container instanceof Array) && container[key] !== undefined) {
        return container[key];
      }
    }

    return null;
  }

  normalizePublicBucketsResponse(res: any, sectionKey?: string): Array<any> {
    const rawBuckets = this.getPublicResultValue(res);
    const buckets = [];

    if (!(rawBuckets instanceof Array)) {
      return buckets;
    }

    for (let i = 0; i < rawBuckets.length; i++) {
      const bucket = this.normalizePublicBucket(rawBuckets[i], true, sectionKey);
      if (bucket && bucket.lists.length > 0 && !this.isHiddenPublicBucket(bucket)) {
        buckets.push(bucket);
      }
    }

    return this.sortPublicBuckets(buckets);
  }

  isHiddenPublicBucket(bucket: any): boolean {
    return bucket && bucket.id === "-5";
  }

  sortPublicBuckets(buckets: Array<any>): Array<any> {
    const sorted = [];
    this.appendPublicBucketById(sorted, buckets, "-1");
    this.appendPublicBucketById(sorted, buckets, "-3");

    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i].id !== "-1" && buckets[i].id !== "-3") {
        sorted.push(buckets[i]);
      }
    }

    return sorted;
  }

  appendPublicBucketById(target: Array<any>, buckets: Array<any>, bucketId: string) {
    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i].id === bucketId) {
        target.push(buckets[i]);
        return;
      }
    }
  }

  appendUniquePublicBuckets(existingBuckets: Array<any>, newBuckets: Array<any>): Array<any> {
    const buckets = existingBuckets ? existingBuckets.slice(0) : [];
    if (!(newBuckets instanceof Array)) {
      return this.sortPublicBuckets(buckets);
    }

    for (let i = 0; i < newBuckets.length; i++) {
      const newBucket = newBuckets[i];
      if (!newBucket || newBucket.id === undefined || newBucket.id === null) {
        continue;
      }

      let exists = false;
      for (let j = 0; j < buckets.length; j++) {
        if (String(buckets[j].id) === String(newBucket.id)) {
          exists = true;
          break;
        }
      }

      if (!exists) {
        buckets.push(newBucket);
      }
    }

    return this.sortPublicBuckets(buckets);
  }

  normalizePublicBucket(rawBucket: any, sliceLists: boolean = true, sectionKey?: string): any {
    if (!rawBucket) {
      return null;
    }

    const id = rawBucket.ID !== undefined && rawBucket.ID !== null ? rawBucket.ID :
      rawBucket.id !== undefined && rawBucket.id !== null ? rawBucket.id :
      rawBucket.tagId !== undefined && rawBucket.tagId !== null ? rawBucket.tagId : rawBucket.tagID;

    if (id === undefined || id === null) {
      return null;
    }

    const lists = this.filterPublicListsForSection(sectionKey, this.normalizeListsResponse(rawBucket.lists || rawBucket.results || rawBucket.items || []));
    const maxResults = this.normalizeNumber(rawBucket.maxResults || rawBucket.maxResult || rawBucket.count || lists.length);

    return {
      id: String(id),
      title: this.getPublicBucketTitle(rawBucket, String(id)),
      lists: sliceLists ? lists.slice(0, 10) : lists,
      maxResults: maxResults
    };
  }

  reconcilePublicSections() {
    const communitySection = this.getPublicSectionByKey("community");
    if (!communitySection || !communitySection.buckets) {
      return;
    }

    const buckets = [];
    for (let i = 0; i < communitySection.buckets.length; i++) {
      const bucket = communitySection.buckets[i];
      const lists = this.filterPublicListsForSection("community", bucket.lists || []);
      if (lists.length > 0) {
        bucket.lists = lists;
        buckets.push(bucket);
      }
    }
    communitySection.buckets = buckets;
  }

  filterPublicListsForSection(sectionKey: string, lists: Array<any>): Array<any> {
    if (!(lists instanceof Array)) {
      return [];
    }

    if (sectionKey === "community") {
      return lists.filter((list) => {
        return !this.isCheapChartsCuratedList(list) && !this.isListInCuratedDiscovery(list);
      });
    }

    if (sectionKey === "curated") {
      return lists.filter((list) => {
        return !this.hasPublicListSourceFields(list) || this.isCheapChartsCuratedList(list);
      });
    }

    return lists;
  }

  isListInCuratedDiscovery(list: any): boolean {
    const curatedSection = this.getPublicSectionByKey("curated");
    const listKey = this.getPublicListDedupeKey(list);
    if (!curatedSection || !curatedSection.buckets || !listKey) {
      return false;
    }

    for (let i = 0; i < curatedSection.buckets.length; i++) {
      const lists = curatedSection.buckets[i].lists || [];
      for (let j = 0; j < lists.length; j++) {
        if (this.getPublicListDedupeKey(lists[j]) === listKey) {
          return true;
        }
      }
    }
    return false;
  }

  getPublicListDedupeKey(list: any): string {
    const listId = this.getListId(list);
    if (listId) {
      return "id:" + listId;
    }

    const title = this.getTitle(list);
    return title ? "title:" + String(title).toLowerCase().trim() : "";
  }

  hasPublicListSourceFields(list: any): boolean {
    if (!list) {
      return false;
    }

    return list.publisher !== undefined ||
      list.publisherName !== undefined ||
      list.source !== undefined ||
      list.sourceName !== undefined ||
      list.type !== undefined ||
      list.listType !== undefined ||
      list.isCurated !== undefined ||
      list.curated !== undefined ||
      list.editorial !== undefined ||
      list.author !== undefined ||
      list.owner !== undefined ||
      list.authorName !== undefined ||
      list.username !== undefined ||
      list.userName !== undefined ||
      this.getPublicListUserData(list) !== null;
  }

  isCheapChartsCuratedList(list: any): boolean {
    if (!list) {
      return false;
    }

    if (this.isTruthyListValue(list.isCurated) || this.isTruthyListValue(list.curated) || this.isTruthyListValue(list.editorial)) {
      return true;
    }

    const typeValues = [
      list.type,
      list.listType,
      list.source,
      list.sourceName
    ];
    for (let i = 0; i < typeValues.length; i++) {
      const typeValue = this.normalizePublicListSourceName(typeValues[i]);
      if (typeValue === "curated" || typeValue === "editorial" || typeValue === "cheapcharts") {
        return true;
      }
    }

    const authorValues = [
      this.getPublicListAuthor(list),
      list.publisherName,
      list.publisher
    ];
    for (let j = 0; j < authorValues.length; j++) {
      if (this.isCheapChartsSourceName(authorValues[j])) {
        return true;
      }
    }

    return false;
  }

  normalizePublicListSourceName(value: any): string {
    if (value === undefined || value === null) {
      return "";
    }
    return String(value).toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  isCheapChartsSourceName(value: any): boolean {
    const sourceName = this.normalizePublicListSourceName(value);
    return sourceName === "cheapcharts" ||
      sourceName === "cheapchartshq" ||
      sourceName === "cheapchartsteam" ||
      sourceName === "cheapchartseditorial";
  }

  getPublicResultValue(res: any): any {
    if (!res) {
      return null;
    }
    if (res.result !== undefined) {
      return res.result;
    }
    if (res.results !== undefined) {
      if (res.results.result !== undefined) {
        return res.results.result;
      }
      if (res.results.results !== undefined) {
        return res.results.results;
      }
      return res.results;
    }
    return res;
  }

  getPublicBucketTitle(bucket: any, bucketId: string): string {
    if (!bucket) {
      return "";
    }

    const language = this.getPublicBucketLanguage();
    return bucket[language] || bucket.en || bucket.de || bucket.fr || bucket.es ||
      bucket.title || bucket.name || bucket.label || this.getSpecialPublicBucketTitle(bucketId);
  }

  getPublicBucketLanguage(): string {
    const country = Global.getCountry();
    if (country === "de" || country === "fr" || country === "es") {
      return country;
    }
    return "en";
  }

  getSpecialPublicBucketTitle(bucketId: string): string {
    if (bucketId === "-1") {
      return "Recently Added";
    }
    if (bucketId === "-3") {
      return "All Lists";
    }
    if (bucketId === "-4") {
      return "Iconic Lists";
    }
    if (bucketId === "-5") {
      return "Christmas";
    }
    return this.getString("publicLists");
  }

  normalizeNumber(value: any): number {
    if (value === undefined || value === null || value === "") {
      return 0;
    }

    const numberValue = Number(value);
    return isNaN(numberValue) ? 0 : numberValue;
  }

  openPublicCategory(section: any, bucket: any) {
    if (!section || !bucket) {
      return;
    }

    const url = this.getPublicCategoryUrl(bucket);
    this.savePublicCategoryRouteMarker(url, section.key);
    this.saveListsScrollPosition(true, url);
    if (url && this.router.url.split("?")[0] !== url) {
      this.router.navigateByUrl(url);
      return;
    }

    this.publicCategoryRequestId++;
    this.selectedPublicSection = section;
    this.selectedPublicBucket = bucket;
    this.publicCategoryLists = [];
    this.publicCategoryHasMore = false;
    this.publicCategoryError = false;
    this.isLoadingPublicCategory = false;
    if (this.restorePublicCategoryCache(section, bucket)) {
      return;
    }
    this.loadPublicCategory(false);
  }

  closePublicCategory(updateUrl?: boolean, restoreAfterClose?: boolean) {
    const restoreTargetUrl = this.getPublicCategoryRestoreTargetUrl();
    this.publicCategoryRequestId++;
    this.selectedPublicSection = null;
    this.selectedPublicBucket = null;
    this.publicCategoryLists = [];
    this.isLoadingPublicCategory = false;
    this.publicCategoryError = false;
    this.publicCategoryHasMore = false;
    this.routePublicCategoryId = "";
    this.routePublicCategorySlug = "";

    if (updateUrl) {
      this.router.navigateByUrl(this.getListSectionUrl(this.activeListSection)).then(() => {
        this.prepareListsScrollRestore(restoreTargetUrl);
      });
    }
    else if (restoreAfterClose === true) {
      this.prepareListsScrollRestore(restoreTargetUrl);
    }
  }

  getPublicCategoryRestoreTargetUrl(): string {
    if (this.routePublicCategoryId) {
      return this.getPublicCategoryUrlById(this.routePublicCategoryId, this.routePublicCategorySlug || this.routePublicCategoryId);
    }
    if (this.selectedPublicBucket) {
      return this.getPublicCategoryUrl(this.selectedPublicBucket);
    }
    return "";
  }

  activatePublicCategoryFromRoute() {
    if (!this.routePublicCategoryId) {
      return;
    }

    const matchedBucket = this.findPublicBucketById(this.routePublicCategoryId);
    const matchedSection = matchedBucket ? this.findPublicSectionForBucket(this.routePublicCategoryId) : null;
    const section = matchedSection || this.getActivePublicSection() || this.getPublicSectionByKey("curated");
    const title = matchedBucket ? matchedBucket.title : this.getTitleFromSlug(this.routePublicCategorySlug);
    const bucket = matchedBucket || {
      id: this.routePublicCategoryId,
      title: title || this.routePublicCategoryId,
      lists: [],
      maxResults: 0
    };

    if (section) {
      this.activeListSection = section.key;
    }

    this.publicCategoryRequestId++;
    this.selectedPublicSection = section;
    this.selectedPublicBucket = bucket;
    this.publicCategoryLists = [];
    this.publicCategoryHasMore = false;
    this.publicCategoryError = false;
    this.isLoadingPublicCategory = false;
    if (this.restorePublicCategoryCache(section, bucket)) {
      return;
    }
    this.loadPublicCategory(false);
  }

  syncRoutePublicCategoryWithLoadedBuckets() {
    if (!this.routePublicCategoryId || !this.selectedPublicBucket) {
      return;
    }

    const bucket = this.findPublicBucketById(this.routePublicCategoryId);
    const section = this.findPublicSectionForBucket(this.routePublicCategoryId);
    if (bucket && section) {
      this.selectedPublicSection = section;
      this.selectedPublicBucket = bucket;
    }
  }

  findPublicSectionForBucket(bucketId: string): any {
    if (!this.publicSections) {
      return null;
    }

    for (let i = 0; i < this.publicSections.length; i++) {
      const buckets = this.publicSections[i].buckets || [];
      for (let j = 0; j < buckets.length; j++) {
        if (String(buckets[j].id) === String(bucketId)) {
          return this.publicSections[i];
        }
      }
    }
    return null;
  }

  findPublicBucketById(bucketId: string): any {
    const section = this.findPublicSectionForBucket(bucketId);
    if (!section || !section.buckets) {
      return null;
    }

    for (let i = 0; i < section.buckets.length; i++) {
      if (String(section.buckets[i].id) === String(bucketId)) {
        return section.buckets[i];
      }
    }
    return null;
  }

  loadMorePublicCategory() {
    this.loadPublicCategory(true);
  }

  loadPublicCategory(append: boolean) {
    if (!this.selectedPublicSection || !this.selectedPublicBucket || this.isLoadingPublicCategory) {
      return;
    }

    const requestId = ++this.publicCategoryRequestId;
    const offset = append ? this.publicCategoryLists.length : 0;

    this.isLoadingPublicCategory = true;
    this.publicCategoryError = false;

    const request = Global.getStore() === Global.ITUNES ?
      this.customListService.getPublicListsByTag(
        Global.getCountry(),
        Global.getStore(),
        Global.getMediaTypeForWishlistRequest(),
        this.selectedPublicSection.publisher,
        this.publicCategoryLimit,
        offset,
        this.selectedPublicBucket.id
      ) :
      this.customListService.getPublicLists(
        Global.getCountry(),
        Global.getStore(),
        Global.getMediaTypeForWishlistRequest(),
        this.selectedPublicSection.publisher,
        this.selectedPublicBucket.id,
        this.publicCategoryLimit,
        offset,
        ""
      );

    request.subscribe(res => {
      if (requestId !== this.publicCategoryRequestId) {
        return;
      }

      const category = this.normalizePublicCategoryResponse(res, this.selectedPublicSection.key);
      this.publicCategoryLists = append ? this.appendUniquePublicLists(this.publicCategoryLists, category.lists) : category.lists;
      const maxResults = category.maxResults || this.selectedPublicBucket.maxResults;
      this.publicCategoryHasMore = maxResults > 0 ?
        this.publicCategoryLists.length < maxResults :
        category.lists.length >= this.publicCategoryLimit;
      this.isLoadingPublicCategory = false;
      this.savePublicCategoryCache(false, "", true);
    }, err => {
      if (requestId !== this.publicCategoryRequestId) {
        return;
      }
      Log.getInstance().d(err);
      this.publicCategoryError = true;
      this.isLoadingPublicCategory = false;
    });
  }

  normalizePublicCategoryResponse(res: any, sectionKey?: string): any {
    const raw = this.getPublicResultValue(res);
    let lists = [];
    let maxResults = 0;

    if (raw instanceof Array) {
      let selectedBucket = null;
      for (let i = 0; i < raw.length; i++) {
        const bucket = this.normalizePublicBucket(raw[i], false, sectionKey);
        if (bucket && this.selectedPublicBucket && bucket.id === this.selectedPublicBucket.id) {
          selectedBucket = bucket;
          break;
        }
      }
      if (!selectedBucket && raw.length > 0) {
        selectedBucket = this.normalizePublicBucket(raw[0], false, sectionKey);
      }
      if (selectedBucket) {
        lists = selectedBucket.lists;
        maxResults = selectedBucket.maxResults;
      }
      else {
        lists = this.normalizeListsResponse(raw);
      }
    }
    else if (raw) {
      lists = this.filterPublicListsForSection(sectionKey, this.normalizeListsResponse(raw.lists || raw.results || raw.items || raw));
      maxResults = this.normalizeNumber(raw.maxResults || raw.maxResult || raw.count || 0);
    }

    return {
      lists: lists,
      maxResults: maxResults
    };
  }

  appendUniquePublicLists(existingLists: Array<any>, newLists: Array<any>): Array<any> {
    const lists = existingLists ? existingLists.slice(0) : [];
    if (!(newLists instanceof Array)) {
      return lists;
    }

    for (let i = 0; i < newLists.length; i++) {
      const newKey = this.getPublicListDedupeKey(newLists[i]);
      let exists = false;
      for (let j = 0; j < lists.length; j++) {
        if (newKey && this.getPublicListDedupeKey(lists[j]) === newKey) {
          exists = true;
          break;
        }
      }
      if (!exists) {
        lists.push(newLists[i]);
      }
    }
    return lists;
  }

  openPendingEditList() {
    if (!this.pendingEditListId || !this.lists || this.lists.length === 0 || !this.createListModal) {
      return;
    }

    const pendingListId = this.pendingEditListId;
    for (let i = 0; i < this.lists.length; i++) {
      if (this.getListId(this.lists[i]) === pendingListId) {
        this.pendingEditListId = "";
        this.editList(this.lists[i], null);
        this.router.navigateByUrl(this.getListsUrl());
        return;
      }
    }
  }

  openCreateListModal() {
    this.editingListId = "";
    this.resetCreateListForm();
    this.storeCreateListInitialState();
    this.loadCustomListTags();
    this.createListModal.open();
  }

  resetCreateListForm() {
    this.itemSearchTerm = "";
    this.itemSuggestions = [];
    this.selectedItems = [];
    this.selectedTagIds = [];
    this.showTags = false;
    this.draggedItemIndex = null;
    this.createListError = false;
    this.isSavingList = false;
    this.createListForm = {
      title: "",
      description: "",
      isPublic: false,
      rankingMode: false,
      rankingOrder: "asc",
      priceDropNotification: false,
      fourKNotification: false
    };
  }

  loadCustomListTags() {
    this.isLoadingTags = true;
    this.tagsError = false;
    this.tagGroups = [];

    this.customListService.getCustomListTags(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken
    ).subscribe(res => {
      this.tagGroups = this.normalizeTagGroupsResponse(res);
      this.isLoadingTags = false;
    }, err => {
      Log.getInstance().d(err);
      this.tagsError = true;
      this.isLoadingTags = false;
    });
  }

  normalizeTagGroupsResponse(res: any): Array<any> {
    let rawGroups = null;
    const groups = [];

    if (res && res.results) {
      rawGroups = res.results.results || res.results.tags || res.results;
    }
    else if (res) {
      rawGroups = res.results || res.tags || res;
    }

    if (!rawGroups) {
      return groups;
    }

    if (rawGroups instanceof Array) {
      for (let i = 0; i < rawGroups.length; i++) {
        const group = rawGroups[i];
        const title = group.title || group.name || group.category || group.section || this.getString("tags");
        const tags = this.normalizeTagArray(group.tags || group.items || group.results || group);
        if (tags.length > 0) {
          groups.push({ title: title, tags: tags });
        }
      }
      return groups;
    }

    const keys = Object.keys(rawGroups);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const tags = this.normalizeTagArray(rawGroups[key]);
      if (tags.length > 0) {
        groups.push({ title: key, tags: tags });
      }
    }

    return groups;
  }

  normalizeTagArray(rawTags: any): Array<any> {
    const tags = [];

    if (!rawTags) {
      return tags;
    }

    if (rawTags instanceof Array) {
      for (let i = 0; i < rawTags.length; i++) {
        const tag = this.normalizeTag(rawTags[i], null);
        if (tag) {
          tags.push(tag);
        }
      }
      return tags;
    }

    if (typeof rawTags === "object") {
      const keys = Object.keys(rawTags);
      for (let i = 0; i < keys.length; i++) {
        const tag = this.normalizeTag(rawTags[keys[i]], keys[i]);
        if (tag) {
          tags.push(tag);
        }
      }
    }

    return tags;
  }

  normalizeTag(rawTag: any, fallbackId: any): any {
    if (!rawTag && rawTag !== 0) {
      return null;
    }

    if (typeof rawTag !== "object") {
      return {
        id: this.normalizeTagId(fallbackId !== null && fallbackId !== undefined ? fallbackId : rawTag),
        label: String(rawTag)
      };
    }

    const id = rawTag.ID || rawTag.id || rawTag.tagID || rawTag.tagId || rawTag.value || fallbackId;
    const label = rawTag.title || rawTag.name || rawTag.label || rawTag.tag || rawTag.text || id;

    if (id === undefined || id === null) {
      return null;
    }

    return {
      id: this.normalizeTagId(id),
      label: String(label)
    };
  }

  normalizeTagId(id: any): any {
    if (typeof id === "string" && /^-?\d+$/.test(id)) {
      return parseInt(id, 10);
    }
    return id;
  }

  onItemSearchKeyUp() {
    if (this.itemSearchTimeout) {
      clearTimeout(this.itemSearchTimeout);
    }

    this.itemSearchTimeout = setTimeout(() => {
      this.getItemSuggestions();
    }, 300);
  }

  getItemSuggestions() {
    const query = (this.itemSearchTerm || "").trim();

    if (query.length < 2) {
      this.itemSuggestions = [];
      return;
    }

    this.isSearchingItems = true;
    const suggestionMediaType = this.getSuggestionMediaType();
    const request = this.shouldUseItemSearchForSuggestions(suggestionMediaType) ?
      this.searchService.searchForItems(
        Global.getStore(),
        suggestionMediaType,
        Global.getCountry(),
        query,
        0,
        10
      ) :
      this.searchService.getSuggestions(
        Global.getStore(),
        suggestionMediaType,
        Global.getCountry(),
        query,
        0,
        10
      );

    request.subscribe(res => {
      if ((this.itemSearchTerm || "").trim() === query) {
        this.itemSuggestions = this.normalizeSuggestionResults(res, suggestionMediaType);
      }
      this.isSearchingItems = false;
    }, err => {
      Log.getInstance().d(err);
      this.itemSuggestions = [];
      this.isSearchingItems = false;
    });
  }

  getSuggestionMediaType(): string {
    const mediaType = Global.getMediaTypeForWishlistRequest();
    if (mediaType === Global.TV) {
      return Global.SEASONS;
    }
    if (mediaType === Global.MUSIC) {
      return Global.ALBUMS;
    }
    return mediaType;
  }

  shouldUseItemSearchForSuggestions(mediaType: string): boolean {
    return Global.getStore() === Global.ITUNES && (mediaType === Global.APPS || mediaType === Global.MACAPPS);
  }

  normalizeSuggestionResults(res: any, mediaType: string): Array<any> {
    if (!res) {
      return [];
    }

    const mediaTypeKeys = this.getSuggestionMediaTypeKeys(mediaType);
    const containers = [
      res && res.results,
      res && res.result,
      res
    ];

    for (let i = 0; i < containers.length; i++) {
      const container = containers[i];
      const normalizedResults = this.getSuggestionResultsFromContainer(container, mediaTypeKeys);
      if (normalizedResults.length > 0) {
        return normalizedResults;
      }
    }

    return [];
  }

  getSuggestionMediaTypeKeys(mediaType: string): Array<string> {
    const keys = [];
    this.addUniqueSuggestionMediaTypeKey(keys, mediaType);
    this.addUniqueSuggestionMediaTypeKey(keys, Global.getMediaTypeForWishlistRequest());

    if (mediaType === Global.SEASONS || mediaType === Global.TV) {
      this.addUniqueSuggestionMediaTypeKey(keys, Global.SEASONS);
      this.addUniqueSuggestionMediaTypeKey(keys, Global.TV);
    }
    if (mediaType === Global.MOVIES || mediaType === Global.BUYMOVIES || mediaType === Global.RENTALMOVIES) {
      this.addUniqueSuggestionMediaTypeKey(keys, Global.MOVIES);
    }

    return keys;
  }

  addUniqueSuggestionMediaTypeKey(keys: Array<string>, key: string) {
    if (key && keys.indexOf(key) === -1) {
      keys.push(key);
    }
  }

  getSuggestionResultsFromContainer(container: any, mediaTypeKeys: Array<string>): Array<any> {
    if (!container) {
      return [];
    }

    if (container instanceof Array) {
      return container;
    }

    if (container.results instanceof Array) {
      return container.results;
    }

    if (container.items instanceof Array) {
      return container.items;
    }

    if (container.result instanceof Array) {
      return container.result;
    }

    for (let i = 0; i < mediaTypeKeys.length; i++) {
      const mediaTypeKey = mediaTypeKeys[i];
      if (container[mediaTypeKey] instanceof Array) {
        return container[mediaTypeKey];
      }
    }

    return [];
  }

  addSuggestion(suggestion: any) {
    const itemId = this.getSuggestionId(suggestion);
    if (itemId === undefined || itemId === null || itemId === "") {
      return;
    }

    for (let i = 0; i < this.selectedItems.length; i++) {
      if (String(this.getSuggestionId(this.selectedItems[i])) === String(itemId)) {
        this.itemSearchTerm = "";
        this.itemSuggestions = [];
        return;
      }
    }

    this.selectedItems.push(suggestion);
    this.itemSearchTerm = "";
    this.itemSuggestions = [];
  }

  removeSelectedItem(index: number) {
    this.selectedItems.splice(index, 1);
  }

  onSelectedItemDragStart(index: number) {
    this.draggedItemIndex = index;
  }

  onSelectedItemDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onSelectedItemDrop(index: number) {
    if (this.draggedItemIndex === null || this.draggedItemIndex === index) {
      this.draggedItemIndex = null;
      return;
    }

    const draggedItem = this.selectedItems.splice(this.draggedItemIndex, 1)[0];
    this.selectedItems.splice(index, 0, draggedItem);
    this.draggedItemIndex = null;
  }

  onSelectedItemDragEnd() {
    this.draggedItemIndex = null;
  }

  onSelectedItemTouchStart(event: TouchEvent, index: number) {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    this.touchDragIndex = index;
    this.draggedItemIndex = index;
  }

  onSelectedItemTouchMove(event: TouchEvent) {
    if (this.touchDragIndex === null || !event || !event.touches || event.touches.length === 0) {
      return;
    }

    event.preventDefault();
    const touch = event.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    const targetIndex = this.getSelectedItemIndexFromElement(target);
    if (targetIndex !== null && targetIndex !== this.touchDragIndex) {
      const draggedItem = this.selectedItems.splice(this.touchDragIndex, 1)[0];
      this.selectedItems.splice(targetIndex, 0, draggedItem);
      this.touchDragIndex = targetIndex;
      this.draggedItemIndex = targetIndex;
    }
  }

  onSelectedItemTouchEnd(event: TouchEvent) {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    this.touchDragIndex = null;
    this.draggedItemIndex = null;
  }

  getSelectedItemIndexFromElement(element: Element): number {
    let current: any = element;
    while (current) {
      if (current.classList && current.classList.contains('selected-item')) {
        const index = current.getAttribute('data-index');
        if (index !== null && index !== undefined && index !== '') {
          const parsedIndex = parseInt(index, 10);
          return isNaN(parsedIndex) ? null : parsedIndex;
        }
      }
      current = current.parentElement;
    }
    return null;
  }

  getSuggestionId(suggestion: any): any {
    if (!suggestion) {
      return null;
    }
    return suggestion.idInStore || suggestion.itemId || suggestion.itemID || suggestion.asin || suggestion.ASIN ||
      suggestion.amazonId || suggestion.amazonID || suggestion.itemASIN || suggestion.id;
  }

  getNormalizedItemId(suggestion: any): any {
    const itemId = this.getSuggestionId(suggestion);
    if (typeof itemId === "string" && /^-?\d+$/.test(itemId)) {
      return parseInt(itemId, 10);
    }
    return itemId;
  }

  getSuggestionItemType(suggestion: any): number {
    if (suggestion && suggestion.itemType !== undefined && suggestion.itemType !== null) {
      const itemType = this.normalizeItemType(suggestion.itemType);
      if (itemType !== null) {
        return itemType;
      }
    }

    if (suggestion && suggestion.mediaType !== undefined && suggestion.mediaType !== null) {
      const mediaTypeItemType = this.normalizeItemType(suggestion.mediaType);
      if (mediaTypeItemType !== null) {
        return mediaTypeItemType;
      }
    }

    return this.getItemTypeForMediaType(Global.getMediaTypeForWishlistRequest());
  }

  getSuggestionTrackType(suggestion: any): string {
    if (suggestion && suggestion.trackType !== undefined && suggestion.trackType !== null && suggestion.trackType !== "") {
      return String(suggestion.trackType);
    }

    const itemType = this.getSuggestionItemType(suggestion);
    switch (itemType) {
      case 0:
        return "song";
      case 1:
        return "album";
      case 2:
        return "movie";
      case 4:
        return "season";
      case 5:
        return "audiobook";
      case 6:
        return "ebook";
      case 7:
        return "app";
      case 8:
        return "macApp";
      default:
        return this.getTrackTypeForMediaType(Global.getMediaTypeForWishlistRequest());
    }
  }

  normalizeItemType(itemType: any): number {
    if (typeof itemType === "number") {
      return itemType;
    }
    if (typeof itemType === "string" && /^-?\d+$/.test(itemType)) {
      return parseInt(itemType, 10);
    }

    const normalizedItemType = String(itemType || "").toLowerCase();
    switch (normalizedItemType) {
      case "single":
      case "singles":
      case "song":
      case "songs":
        return 0;
      case "album":
      case "albums":
        return 1;
      case "movie":
      case "movies":
        return 2;
      case "episode":
      case "episodes":
        return 3;
      case "season":
      case "seasons":
      case "tv":
      case "tvseason":
      case "tvseasons":
        return 4;
      case "audiobook":
      case "audiobooks":
        return 5;
      case "ebook":
      case "ebooks":
        return 6;
      case "app":
      case "apps":
        return 7;
      case "macapp":
      case "macapps":
        return 8;
      case "game":
      case "games":
        return 9;
      default:
        return null;
    }
  }

  getItemTypeForMediaType(mediaType: string): number {
    if (mediaType === Global.MOVIES) {
      return 2;
    }
    if (mediaType === Global.TV || mediaType === Global.SEASONS) {
      return 4;
    }
    if (mediaType === Global.MUSIC || mediaType === Global.ALBUMS) {
      return 1;
    }
    if (mediaType === Global.APPS) {
      return 7;
    }
    if (mediaType === Global.EBOOKS) {
      return 6;
    }
    if (mediaType === Global.AUDIOBOOKS) {
      return 5;
    }
    if (mediaType === Global.MACAPPS) {
      return 8;
    }
    return 2;
  }

  getTrackTypeForMediaType(mediaType: string): string {
    if (mediaType === Global.MOVIES) {
      return "movie";
    }
    if (mediaType === Global.TV || mediaType === Global.SEASONS) {
      return "season";
    }
    if (mediaType === Global.MUSIC || mediaType === Global.ALBUMS) {
      return "album";
    }
    if (mediaType === Global.APPS) {
      return "app";
    }
    if (mediaType === Global.EBOOKS) {
      return "ebook";
    }
    if (mediaType === Global.AUDIOBOOKS) {
      return "audiobook";
    }
    if (mediaType === Global.MACAPPS) {
      return "macApp";
    }
    return "movie";
  }

  getSuggestionThumbnail(suggestion: any): string {
    if (!suggestion) {
      return "";
    }

    return this.getImageUrlFromValue(suggestion);
  }

  getSuggestionTitle(suggestion: any): string {
    if (!suggestion) {
      return "";
    }
    return suggestion.title || suggestion.itemTitle || suggestion.itemName || suggestion.name || suggestion.trackName || suggestion.collectionName || suggestion.listTitle || "";
  }

  getSuggestionSubtitle(suggestion: any): string {
    if (!suggestion) {
      return "";
    }
    const parts = [];
    if (suggestion.releaseYear) {
      parts.push(suggestion.releaseYear);
    }
    if (suggestion.artistName) {
      parts.push(suggestion.artistName);
    }
    else if (suggestion.artist) {
      parts.push(suggestion.artist);
    }
    else if (suggestion.actors) {
      parts.push(String(suggestion.actors).split(",").slice(0, 2).join(","));
    }
    return parts.join(" · ");
  }

  toggleTag(tagId: any) {
    for (let i = 0; i < this.selectedTagIds.length; i++) {
      if (String(this.selectedTagIds[i]) === String(tagId)) {
        this.selectedTagIds.splice(i, 1);
        return;
      }
    }
    this.selectedTagIds.push(tagId);
  }

  isTagSelected(tagId: any): boolean {
    for (let i = 0; i < this.selectedTagIds.length; i++) {
      if (String(this.selectedTagIds[i]) === String(tagId)) {
        return true;
      }
    }
    return false;
  }

  toggleTags() {
    this.showTags = !this.showTags;
  }

  getSelectedTagCount(): number {
    return this.selectedTagIds.length;
  }

  isCreateListRanked(): boolean {
    return this.createListForm.rankingMode === true;
  }

  getCreateListRankingMode(): number {
    if (!this.isCreateListRanked()) {
      return 0;
    }
    return this.createListForm.rankingOrder === "desc" ? 2 : 1;
  }

  getCreateRankingNumber(index: number): number {
    if (this.createListForm.rankingOrder === "desc") {
      return this.selectedItems.length - index;
    }
    return index + 1;
  }

  storeCreateListInitialState() {
    this.createListInitialState = this.getCreateListFormState();
  }

  getCreateListFormState(): string {
    const items = [];
    for (let i = 0; i < this.selectedItems.length; i++) {
      items.push(this.buildCreateListItemData(this.selectedItems[i], false));
    }

    return JSON.stringify({
      title: (this.createListForm.title || "").trim(),
      description: this.createListForm.description || "",
      isPublic: this.createListForm.isPublic ? 1 : 0,
      rankingMode: this.getCreateListRankingMode(),
      items: items,
      tags: this.selectedTagIds,
      notifications: {
        priceDrop: this.createListForm.priceDropNotification ? 1 : 0,
        "4k": this.createListForm.fourKNotification ? 1 : 0
      }
    });
  }

  hasCreateListUnsavedChanges(): boolean {
    return this.createListInitialState !== this.getCreateListFormState();
  }

  closeCreateListModal() {
    this.createListModal.requestClose();
  }

  setCreateListPublic(value: any) {
    this.createListForm.isPublic = this.isTruthyListValue(value);
  }

  hasCreateListTitle(): boolean {
    return (this.createListForm.title || "").trim().length > 0;
  }

  hasCreateListItems(): boolean {
    return this.selectedItems && this.selectedItems.length > 0;
  }

  shouldShowCreateListItemValidation(): boolean {
    return this.hasCreateListTitle() && !this.hasCreateListItems();
  }

  canSaveCreateList(): boolean {
    return !this.isSavingList && this.hasCreateListTitle() && this.hasCreateListItems();
  }

  saveCreateList() {
    if (!this.canSaveCreateList()) {
      return;
    }

    this.isSavingList = true;
    this.createListError = false;

    const listData = this.buildCreateListData();
    const encodedListData = this.encodeListData(listData);

    const request = this.editingListId ? this.customListService.editCustomList(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      this.editingListId,
      encodedListData
    ) : this.customListService.createCustomList(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      encodedListData
    );

    request.subscribe(res => {
      if (this.isCreateListSuccess(res)) {
        this.createListModal.close();
        this.editingListId = "";
        this.resetCreateListForm();
        this.loadLists();
      }
      else {
        this.createListError = true;
      }
      this.isSavingList = false;
    }, err => {
      Log.getInstance().d(err);
      this.createListError = true;
      this.isSavingList = false;
    });
  }

  buildCreateListData(): any {
    const items = [];
    for (let i = 0; i < this.selectedItems.length; i++) {
      items.push(this.buildCreateListItemData(this.selectedItems[i], true));
    }

    return {
      title: (this.createListForm.title || "").trim(),
      description: this.createListForm.description || "",
      isPublic: this.createListForm.isPublic ? 1 : 0,
      rankingMode: this.getCreateListRankingMode(),
      items: items,
      tags: this.selectedTagIds,
      notifications: {
        priceDrop: this.createListForm.priceDropNotification ? 1 : 0,
        "4k": this.createListForm.fourKNotification ? 1 : 0
      }
    };
  }

  encodeListData(listData: any): string {
    return btoa(unescape(encodeURIComponent(JSON.stringify(listData))));
  }

  buildCreateListItemData(item: any, includeNotes: boolean): any {
    const itemId = this.getNormalizedItemId(item);
    const itemData: any = {};

    if (Global.getStore() === Global.ITUNES) {
      itemData.itemID = itemId;
      itemData.trackType = this.getSuggestionTrackType(item);
    }
    else {
      itemData.idInStore = itemId;
      itemData.itemType = this.getSuggestionItemType(item);
    }

    if (includeNotes) {
      itemData.notes = this.getItemNotes(item);
    }

    return itemData;
  }

  getItemNotes(item: any): string {
    if (!item) {
      return "";
    }
    return item.notes || item.note || "";
  }

  isCreateListSuccess(res: any): boolean {
    return res === "success" ||
      (res && (res.status === "success" ||
      res.message === "success" ||
      res.success === true ||
      res.success === "true" ||
      (res.results && res.results.status === "success")));
  }

  normalizeListsResponse(res: any): Array<any> {
    if (res instanceof Array) {
      return res;
    }

    if (!res) {
      return [];
    }

    if (res.results) {
      if (res.results instanceof Array) {
        return res.results;
      }
      if (res.results.results instanceof Array) {
        return res.results.results;
      }
      if (res.results.lists instanceof Array) {
        return res.results.lists;
      }
      if (res.results.customLists instanceof Array) {
        return res.results.customLists;
      }
    }

    if (res.customLists instanceof Array) {
      return res.customLists;
    }

    if (res.lists instanceof Array) {
      return res.lists;
    }

    return [];
  }

  initMyListsSort() {
    const storage = StorageHelper.getInstance();
    const savedSort = storage.get(MyListsComponent.SORT_STORAGE_KEY) ||
      storage.getFilter(Global.getStore(), Global.getMediaType(), "my-lists", StorageHelper.keys.sort);
    this.sortKey = this.isValidMyListsSort(savedSort) ? savedSort : MyListsComponent.SORT_AZ;
  }

  sortChanged(sortKey: string) {
    this.sortKey = this.isValidMyListsSort(sortKey) ? sortKey : MyListsComponent.SORT_AZ;
    const storage = StorageHelper.getInstance();
    storage.save(MyListsComponent.SORT_STORAGE_KEY, this.sortKey);
    storage.saveFilter(Global.getStore(), Global.getMediaType(), "my-lists", StorageHelper.keys.sort, this.sortKey);
    this.lists = this.sortLists(this.lists);
  }

  isValidMyListsSort(sortKey: string): boolean {
    return this.sortingValues.indexOf(sortKey) >= 0;
  }

  sortLists(lists: Array<any>): Array<any> {
    const sortedLists = lists instanceof Array ? lists.slice(0) : [];
    sortedLists.sort((a, b) => this.compareLists(a, b));
    return sortedLists;
  }

  compareLists(a: any, b: any): number {
    let result = 0;

    if (this.sortKey === MyListsComponent.SORT_CREATED) {
      result = this.compareNumbersDescending(this.getListCreatedTime(a), this.getListCreatedTime(b), true);
    }
    else if (this.sortKey === MyListsComponent.SORT_MODIFIED) {
      result = this.compareNumbersDescending(this.getListModifiedTime(a), this.getListModifiedTime(b), true);
    }
    else if (this.sortKey === MyListsComponent.SORT_LENGTH) {
      result = this.compareNumbersDescending(this.getItemCountNumber(a), this.getItemCountNumber(b), false);
    }

    return result || this.compareListTitles(a, b) || this.compareListIds(a, b);
  }

  compareListTitles(a: any, b: any): number {
    const titleA = String(this.getTitle(a) || "").toLowerCase();
    const titleB = String(this.getTitle(b) || "").toLowerCase();
    return titleA.localeCompare(titleB);
  }

  compareListIds(a: any, b: any): number {
    const idA = String(this.getListId(a) || "");
    const idB = String(this.getListId(b) || "");
    return idA.localeCompare(idB);
  }

  compareNumbersDescending(valueA: number, valueB: number, missingLast: boolean): number {
    if (valueA === valueB) {
      return 0;
    }
    if (missingLast) {
      if (!valueA) {
        return 1;
      }
      if (!valueB) {
        return -1;
      }
    }
    return valueA > valueB ? -1 : 1;
  }

  getItemCountNumber(list: any): number {
    return this.normalizeNumber(this.getItemCount(list));
  }

  getListCreatedTime(list: any): number {
    return this.getListDateTime(list, [
      "createdAt",
      "insertDate",
      "dateCreated",
      "creationDate",
      "createdDate",
      "createdOn",
      "created",
      "createDate",
      "insertedAt",
      "addedAt",
      "addedOn"
    ]);
  }

  getListModifiedTime(list: any): number {
    return this.getListDateTime(list, [
      "updatedAt",
      "updateDate",
      "dateUpdated",
      "lastModifiedAt",
      "lastModified",
      "lastUpdated",
      "updatedOn",
      "updated",
      "modifiedAt",
      "modifiedOn",
      "modified",
      "changedAt"
    ]);
  }

  getListDateTime(list: any, keys: Array<string>): number {
    return this.normalizeDateTime(this.getFirstListFieldValue(list, keys));
  }

  getFirstListFieldValue(list: any, keys: Array<string>): any {
    if (!list || !(keys instanceof Array)) {
      return null;
    }

    const containers = [
      list,
      list.data,
      list.results,
      list.additionalInfo,
      list.customList,
      list.list
    ];

    for (let i = 0; i < containers.length; i++) {
      const container = containers[i];
      if (!container || typeof container !== "object") {
        continue;
      }

      for (let j = 0; j < keys.length; j++) {
        const value = container[keys[j]];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
    }
    return null;
  }

  normalizeDateTime(value: any): number {
    if (value === undefined || value === null || value === "") {
      return 0;
    }

    if (typeof value === "object") {
      return this.normalizeDateTime(this.getFirstListFieldValue(value, [
        "value",
        "date",
        "dateTime",
        "timestamp",
        "time"
      ]));
    }

    const numberValue = Number(value);
    if (!isNaN(numberValue) && numberValue > 0) {
      return numberValue < 1000000000000 ? numberValue * 1000 : numberValue;
    }

    const parsedDate = Date.parse(String(value));
    return isNaN(parsedDate) ? 0 : parsedDate;
  }

  getTitle(list: any): string {
    if (!list) {
      return "";
    }
    return list.title || list.name || list.listName || "";
  }

  getListTitleParts(list: any): Array<string> {
    const title = String(this.getTitle(list) || "").replace(/\s+/g, " ").trim();
    if (!title) {
      return [""];
    }

    return this.splitListTitle(title);
  }

  splitListTitle(title: string): Array<string> {
    if (!title || title.length <= 28) {
      return [title || ""];
    }

    const separatorIndex = this.getListTitleSeparatorIndex(title);
    if (separatorIndex > 0) {
      const separatorLength = this.getListTitleSeparatorLength(title, separatorIndex);
      const firstPart = title.substr(0, separatorIndex).trim();
      const secondPart = title.substr(separatorIndex + separatorLength).trim();
      if (firstPart && secondPart) {
        return [firstPart, secondPart];
      }
    }

    const splitIndex = this.getBalancedListTitleSplitIndex(title);
    if (splitIndex > 0) {
      return [
        title.substr(0, splitIndex).trim(),
        title.substr(splitIndex + 1).trim()
      ];
    }

    return [title];
  }

  getListTitleSeparatorIndex(title: string): number {
    const separators = [": ", " - ", " | ", " / "];
    let bestIndex = -1;
    let bestDistance = title.length;
    const targetIndex = Math.floor(title.length / 2);

    for (let i = 0; i < separators.length; i++) {
      let index = title.indexOf(separators[i]);
      while (index >= 0) {
        const firstLength = index;
        const secondLength = title.length - index - separators[i].length;
        if (firstLength >= 4 && secondLength >= 4) {
          const distance = Math.abs(index - targetIndex);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        }
        index = title.indexOf(separators[i], index + separators[i].length);
      }
    }

    return bestIndex;
  }

  getListTitleSeparatorLength(title: string, separatorIndex: number): number {
    const separators = [": ", " - ", " | ", " / "];
    for (let i = 0; i < separators.length; i++) {
      if (title.substr(separatorIndex, separators[i].length) === separators[i]) {
        return separators[i].length;
      }
    }
    return 1;
  }

  getBalancedListTitleSplitIndex(title: string): number {
    const targetIndex = Math.floor(title.length / 2);
    let bestIndex = -1;
    let bestDistance = title.length;

    for (let i = 0; i < title.length; i++) {
      if (title.charAt(i) !== " ") {
        continue;
      }

      const firstLength = i;
      const secondLength = title.length - i - 1;
      if (firstLength < 8 || secondLength < 8) {
        continue;
      }

      const distance = Math.abs(i - targetIndex);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    return bestIndex;
  }

  getDescription(list: any): string {
    if (!list) {
      return "";
    }
    return list.description || list.desc || "";
  }

  getItemCount(list: any): number {
    if (!list) {
      return 0;
    }
    if (list.itemListLength !== undefined && list.itemListLength !== null) {
      return list.itemListLength;
    }
    if (list.itemCount !== undefined && list.itemCount !== null) {
      return list.itemCount;
    }
    if (list.itemsCount !== undefined && list.itemsCount !== null) {
      return list.itemsCount;
    }
    if (list.numberOfItems !== undefined && list.numberOfItems !== null) {
      return list.numberOfItems;
    }
    if (list.count !== undefined && list.count !== null) {
      return list.count;
    }
    if (list.items instanceof Array) {
      return list.items.length;
    }
    return 0;
  }

  getPublicListAuthor(list: any): string {
    if (!list) {
      return "";
    }

    const userData = this.getPublicListUserData(list);
    const candidates = [
      userData && userData.username,
      userData && userData.userName,
      userData && userData.name,
      userData && userData.displayName,
      this.getPublicListAuthorDataValue(list.author),
      this.getPublicListAuthorDataValue(list.owner),
      list.author,
      list.owner,
      list.userName,
      list.username,
      list.publisherName,
      list.publisher
    ];

    for (let i = 0; i < candidates.length; i++) {
      const author = this.normalizePublicListAuthor(candidates[i]);
      if (author) {
        return author;
      }
    }

    return "";
  }

  normalizePublicListAuthor(value: any): string {
    if (value && typeof value === "object") {
      return "";
    }
    const author = value !== undefined && value !== null ? String(value).trim() : "";
    if (!author || /^-?\d+$/.test(author)) {
      return "";
    }
    return author;
  }

  hasPublicListAuthor(list: any): boolean {
    return this.getPublicListAuthor(list) !== "";
  }

  getPublicListUserData(list: any): any {
    if (!list) {
      return null;
    }
    if (list.userData || list.user || list.authorData || list.authorInfo || list.ownerData || list.ownerInfo) {
      return list.userData || list.user || list.authorData || list.authorInfo || list.ownerData || list.ownerInfo;
    }
    if (list.author && typeof list.author === "object") {
      return list.author;
    }
    if (list.owner && typeof list.owner === "object") {
      return list.owner;
    }
    return null;
  }

  getPublicListAuthorImage(list: any): string {
    if (!list) {
      return "";
    }

    const userData = this.getPublicListUserData(list);
    const image = userData ? (userData.profileImage || userData.profileImageUrl || userData.profileImageURL || userData.avatar || userData.image) :
      (list.profileImage || list.profileImageUrl || list.profileImageURL || list.avatar ||
        this.getPublicListAuthorImageDataValue(list.author) || this.getPublicListAuthorImageDataValue(list.owner) || "");
    return image && image !== "placeholder" ? image : "";
  }

  getPublicListAuthorDataValue(value: any): string {
    if (!value || typeof value !== "object") {
      return "";
    }
    return value.username || value.userName || value.name || value.displayName || "";
  }

  getPublicListAuthorImageDataValue(value: any): string {
    if (!value || typeof value !== "object") {
      return "";
    }
    return value.profileImage || value.profileImageUrl || value.profileImageURL || value.avatar || value.image || "";
  }

  hasPublicListAuthorImage(list: any): boolean {
    return this.getPublicListAuthorImage(list) !== "";
  }

  getPublicListLikeCount(list: any): number {
    if (!list) {
      return 0;
    }

    return this.normalizeNumber(
      list.likeCounter !== undefined ? list.likeCounter :
      list.likeCount !== undefined ? list.likeCount :
      list.likes !== undefined ? list.likes :
      list.favoriteCount !== undefined ? list.favoriteCount :
      list.favorites !== undefined ? list.favorites :
      list.upvotes !== undefined ? list.upvotes :
      list.voteCount
    );
  }

  hasPublicBuckets(section: any): boolean {
    return section && section.buckets && section.buckets.length > 0;
  }

  hasAnyPublicBuckets(): boolean {
    if (!this.publicSections) {
      return false;
    }

    for (let i = 0; i < this.publicSections.length; i++) {
      if (this.hasPublicBuckets(this.publicSections[i])) {
        return true;
      }
    }
    return false;
  }

  isAnyPublicSectionLoading(): boolean {
    if (!this.publicSections) {
      return false;
    }

    for (let i = 0; i < this.publicSections.length; i++) {
      if (this.publicSections[i].isLoading) {
        return true;
      }
    }
    return false;
  }

  getListId(list: any): string {
    if (!list) {
      return "";
    }
    const id = list.ID || list.id || list.listId || list.listID || list.customListId || list.customListID;
    if (id === undefined || id === null) {
      return "";
    }
    return String(id);
  }

  getListsUrl(): string {
    return this.getListSectionUrl(this.activeListSection);
  }

  getListSectionUrl(sectionKey: string): string {
    if (sectionKey === "my") {
      return "/" + [
        Global.getCountry(),
        Global.getStoreUrl(),
        this.getString(Global.getMediaTypeForWishlistRequest() + "Url"),
        this.getString("myListsUrl")
      ].join('/');
    }

    return this.getDiscoverListSectionUrl(sectionKey);
  }

  getDiscoverListSectionUrl(sectionKey: string): string {
    const urlKey = sectionKey === "community" ? "communityListsUrl" : "editorialListsUrl";

    return "/" + [
      Global.getCountry(),
      Global.getStoreUrl(),
      this.getString(Global.getMediaTypeForWishlistRequest() + "Url"),
      this.getString("discoverUrl"),
      this.getString(urlKey)
    ].join('/');
  }

  getPublicListsUrl(): string {
    return this.getListSectionUrl("curated");
  }

  getListDetailUrl(list: any): string {
    const listId = this.getListId(list);
    if (!listId) {
      return "";
    }

    return this.getListSectionUrl(this.isMyListsActive() ? "my" : this.activeListSection) + "/" +
      listId + "/" +
      this.getSlug(this.getTitle(list));
  }

  getPublicCategoryUrl(bucket: any): string {
    if (!bucket || bucket.id === undefined || bucket.id === null) {
      return "";
    }

    return this.getPublicCategoryUrlById(String(bucket.id), this.getSlug(bucket.title), this.activeListSection);
  }

  getPublicCategoryUrlById(categoryId: string, slug: string, sectionKey?: string): string {
    if (!categoryId) {
      return "";
    }

    return this.getDiscoverListSectionUrl(sectionKey) + "/" +
      categoryId + "/" +
      (slug || categoryId);
  }

  savePublicCategoryRouteMarker(categoryUrl: string, sectionKey?: string) {
    if (typeof sessionStorage === "undefined" || !categoryUrl) {
      return;
    }

    const path = categoryUrl.split("?")[0].split("#")[0];
    const parts = path.split("/");
    if (parts.length < 7) {
      return;
    }

    try {
      sessionStorage.setItem(this.getPublicCategoryRouteStorageKey(parts, sectionKey), JSON.stringify({
        path: path,
        timestamp: Date.now()
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getPublicCategoryRouteStorageKey(parts: Array<string>, sectionKey?: string): string {
    const hasDiscoverSegment = parts.length > 7 && this.isDiscoverRouteSegment(parts[4]);
    const sectionIndex = hasDiscoverSegment ? 5 : 4;
    const categoryIndex = hasDiscoverSegment ? 6 : 5;
    const slugIndex = hasDiscoverSegment ? 7 : 6;
    const routeSectionKey = sectionKey === "community" || parts[sectionIndex] === this.getString("communityListsUrl") ? "community" : "public";
    return "customListsCategoryRoute:v1:" +
      parts[1] + ":" +
      parts[2] + ":" +
      parts[3] + ":" +
      routeSectionKey + ":" +
      parts[categoryIndex] + ":" +
      parts[slugIndex];
  }

  getSlug(value: any): string {
    let slug = value === undefined || value === null ? "" : String(value).toLowerCase();
    if (slug.normalize) {
      slug = slug.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
    slug = slug.replace(/&/g, " and ");
    slug = slug.replace(/[^a-z0-9]+/g, "-");
    slug = slug.replace(/^-+|-+$/g, "");
    return slug || "list";
  }

  getTitleFromSlug(slug: string): string {
    if (!slug) {
      return "";
    }

    const parts = String(slug).replace(/-/g, " ").split(" ");
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].length > 0) {
        parts[i] = parts[i].charAt(0).toUpperCase() + parts[i].substr(1);
      }
    }
    return parts.join(" ");
  }

  getAbsoluteListDetailUrl(list: any): string {
    return this.getAbsoluteAppUrl(this.getListDetailUrl(list));
  }

  getAbsoluteAppUrl(route: string): string {
    if (!route) {
      return "";
    }
    if (route.indexOf("http://") === 0 || route.indexOf("https://") === 0) {
      return route;
    }

    const origin = window.location.origin || (window.location.protocol + "//" + window.location.host);
    const basePath = this.getBasePath();
    const routePath = route.charAt(0) === "/" ? route.substr(1) : route;
    return origin + basePath + routePath;
  }

  getBasePath(): string {
    let baseHref = "";
    const baseElement = document.querySelector("base");
    if (baseElement) {
      baseHref = baseElement.getAttribute("href") || "";
    }

    if (!baseHref || baseHref === "/" || baseHref === "./") {
      return "/";
    }

    if (baseHref.indexOf("http://") === 0 || baseHref.indexOf("https://") === 0) {
      const link = document.createElement("a");
      link.href = baseHref;
      baseHref = link.pathname || "/";
    }

    if (baseHref.charAt(0) !== "/") {
      baseHref = "/" + baseHref;
    }
    if (baseHref.charAt(baseHref.length - 1) !== "/") {
      baseHref += "/";
    }
    return baseHref;
  }

  getListMenuKey(sectionKey: string, bucketId: any, bucketIndex: number, listIndex: number, list: any): string {
    return [
      sectionKey || "",
      bucketId !== undefined && bucketId !== null ? String(bucketId) : "",
      bucketIndex !== undefined && bucketIndex !== null ? String(bucketIndex) : "",
      listIndex !== undefined && listIndex !== null ? String(listIndex) : "",
      this.getListId(list)
    ].join(":");
  }

  toggleListMenu(list: any, event: Event, menuKey?: string) {
    event.preventDefault();
    event.stopPropagation();

    const listMenuKey = menuKey || this.getListId(list);
    this.activeListMenuId = this.activeListMenuId === listMenuKey ? "" : listMenuKey;
  }

  closeListMenu() {
    this.activeListMenuId = "";
  }

  isListMenuOpen(list: any, menuKey?: string): boolean {
    const listMenuKey = menuKey || this.getListId(list);
    return this.activeListMenuId !== "" && this.activeListMenuId === listMenuKey;
  }

  deleteList(list: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeListMenu();

    const listId = this.getListId(list);
    if (!listId) {
      return;
    }

    this.listToDelete = list;
    this.deleteListConfirmationModal.open();
  }

  onDeleteListConfirmationResult(result) {
    if (result !== ModalComponent.RESULT_YES) {
      this.listToDelete = null;
      return;
    }

    const listId = this.getListId(this.listToDelete);
    if (!listId) {
      this.listToDelete = null;
      return;
    }

    this.customListService.deleteCustomList(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      listId
    ).subscribe(res => {
      if (this.isCreateListSuccess(res)) {
        this.loadLists();
      }
      this.listToDelete = null;
    }, err => {
      Log.getInstance().d(err);
      this.listToDelete = null;
    });
  }

  editList(list: any, event: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.closeListMenu();

    const listId = this.getListId(list);
    if (!listId) {
      return;
    }

    this.editingListId = listId;
    this.resetCreateListForm();
    this.createListForm.title = this.getTitle(list);
    this.createListForm.description = this.getDescription(list);
    this.createListForm.isPublic = this.isPublicList(list);
    this.selectedItems = this.normalizeEditListItems(list);
    this.loadCustomListTags();
    this.loadListForEditing(listId);
    this.storeCreateListInitialState();
    this.createListModal.open();
  }

  loadListForEditing(listId: string) {
    this.customListService.getCustomListDetail(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      listId,
      this.sessionToken
    ).subscribe((res: any) => {
      const detail = res && res.results ? res.results : res;
      if (!detail) {
        return;
      }
      this.createListForm.title = detail.title || this.createListForm.title;
      this.createListForm.description = detail.description || this.createListForm.description;
      this.createListForm.isPublic = this.isTruthyListValue(detail.isPublic);
      this.createListForm.rankingMode = detail.rankingMode && Number(detail.rankingMode) > 0;
      this.createListForm.rankingOrder = Number(detail.rankingMode) === 2 ? "desc" : "asc";
      this.createListForm.priceDropNotification = this.isTruthyListValue(detail.isDropNotification);
      this.createListForm.fourKNotification = this.isTruthyListValue(detail.is4kNotification);
      const detailItems = this.normalizeEditListItems(detail);
      if (detailItems.length > 0 || this.getItemCount(detail) === 0) {
        this.selectedItems = detailItems;
      }
      this.selectedTagIds = detail.tags instanceof Array ? detail.tags : [];
      this.storeCreateListInitialState();
    }, err => {
      Log.getInstance().d(err);
    });
  }

  normalizeEditListItems(detail: any): Array<any> {
    let items: Array<any> = [];
    if (detail.itemData instanceof Array) {
      items = detail.itemData;
    }
    else if (detail.items instanceof Array) {
      items = detail.items;
    }
    else if (detail.itemList instanceof Array) {
      items = detail.itemList;
    }
    else if (detail.listItems instanceof Array) {
      items = detail.listItems;
    }
    else if (detail.itemListData instanceof Array) {
      items = detail.itemListData;
    }
    else if (detail.selectedItems instanceof Array) {
      items = detail.selectedItems;
    }
    else if (detail.data && detail.data.itemData instanceof Array) {
      items = detail.data.itemData;
    }
    else if (detail.data && detail.data.items instanceof Array) {
      items = detail.data.items;
    }

    const normalizedItems = [];
    for (let i = 0; i < items.length; i++) {
      const normalizedItem = this.normalizeEditListItem(items[i]);
      if (this.getSuggestionId(normalizedItem) !== undefined && this.getSuggestionId(normalizedItem) !== null && this.getSuggestionId(normalizedItem) !== "") {
        normalizedItems.push(normalizedItem);
      }
    }
    return normalizedItems;
  }

  normalizeEditListItem(item: any): any {
    const normalized = typeof item === "object" && item !== null ? Object.assign({}, item) : {
      idInStore: item,
      itemID: item
    };
    const thumbnail = this.getSuggestionThumbnail(normalized);
    if (thumbnail && !normalized.thumbnail) {
      normalized.thumbnail = thumbnail;
    }
    const title = this.getSuggestionTitle(normalized);
    if (title && !normalized.title) {
      normalized.title = title;
    }
    return normalized;
  }

  isTruthyListValue(value: any): boolean {
    return value === true || value === 1 || value === "1" || value === "true";
  }

  duplicateList(list: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeListMenu();

    const listId = this.getListId(list);
    if (!listId) {
      return;
    }

    this.customListService.cloneCustomList(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      listId
    ).subscribe(res => {
      if (this.isCreateListSuccess(res)) {
        if (this.isPublicSectionActive()) {
          this.toastService.success(StringHelper.format(this.getString('listDuplicatedToMyLists'), this.getTitle(list)), 5000);
          return;
        }

        this.toastService.success(StringHelper.format(this.getString('listDuplicated'), this.getTitle(list)), 4000);
        this.loadLists();
      }
    }, err => {
      Log.getInstance().d(err);
    });
  }

  shareListLink(list: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeListMenu();

    if (this.isPublicList(list)) {
      this.copyPublicListUrl(list, null);
      return;
    }

    const listId = this.getListId(list);
    if (!listId || !this.sessionToken) {
      Log.getInstance().d('No list ID or session token available for share');
      return;
    }

    const shareTab = BrowserNavigationHelper.openBlankTabForAsyncResult();
    this.customListService.getShareUrl(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      listId
    ).subscribe(res => {
      const shareUrl = this.getShareUrlFromResponse(res);
      if (shareUrl) {
        BrowserNavigationHelper.navigateOpenedTabOrSameWindow(shareTab, shareUrl);
      }
      else {
        BrowserNavigationHelper.closeTabIfOpen(shareTab);
        Log.getInstance().d('Failed to get custom list share URL: ' + JSON.stringify(res));
      }
    }, err => {
      Log.getInstance().d(err);
      BrowserNavigationHelper.closeTabIfOpen(shareTab);
    });
  }

  copyPublicListUrl(list: any, event: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.closeListMenu();

    const url = this.getAbsoluteListDetailUrl(list);
    if (!url) {
      return;
    }

    this.copyTextToClipboard(url).then((copied) => {
      if (copied) {
        this.toastService.success(this.getString("urlCopiedToClipboard"));
      }
      else {
        this.toastService.error(this.getString("technicalProblems"));
      }
    });
  }

  copyTextToClipboard(text: string): Promise<boolean> {
    const navigatorRef = <any>navigator;
    if (navigatorRef && navigatorRef.clipboard && navigatorRef.clipboard.writeText) {
      return navigatorRef.clipboard.writeText(text).then(() => {
        return true;
      }, err => {
        Log.getInstance().d(err);
        return this.copyTextToClipboardFallback(text);
      });
    }

    return Promise.resolve(this.copyTextToClipboardFallback(text));
  }

  copyTextToClipboardFallback(text: string): boolean {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "readonly");
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    let copied = false;
    try {
      copied = document.execCommand("copy");
    }
    catch (err) {
      Log.getInstance().d(err);
    }

    document.body.removeChild(textArea);
    return copied;
  }

  getShareUrlFromResponse(res: any): string {
    if (!res) {
      return "";
    }
    if (res.additionalInfo) {
      if (res.additionalInfo.shareUrl) {
        return res.additionalInfo.shareUrl;
      }
      if (res.additionalInfo.shareURL) {
        return res.additionalInfo.shareURL;
      }
    }
    if (res.shareUrl) {
      return res.shareUrl;
    }
    if (res.shareURL) {
      return res.shareURL;
    }
    if (res.results && res.results.additionalInfo) {
      if (res.results.additionalInfo.shareUrl) {
        return res.results.additionalInfo.shareUrl;
      }
      if (res.results.additionalInfo.shareURL) {
        return res.results.additionalInfo.shareURL;
      }
    }
    if (res.results && res.results.shareUrl) {
      return res.results.shareUrl;
    }
    if (res.results && res.results.shareURL) {
      return res.results.shareURL;
    }
    return "";
  }

  hasPublicValue(list: any): boolean {
    return list && (list.isPublic !== undefined || list.public !== undefined);
  }

  isPublicList(list: any): boolean {
    if (!list) {
      return false;
    }
    const value = list.isPublic !== undefined ? list.isPublic : list.public;
    return value === true || value === 1 || value === "1" || value === "true";
  }

  getThumbs(list: any): Array<string> {
    let thumbs: Array<string> = [];

    if (!list) {
      return thumbs;
    }

    if (list.thumbs instanceof Array) {
      thumbs = this.normalizeThumbs(list.thumbs);
    }
    else if (typeof list.thumbs === "string") {
      thumbs = this.normalizeThumbs(list.thumbs.split(","));
    }
    else if (list.thumbnails instanceof Array) {
      thumbs = this.normalizeThumbs(list.thumbnails);
    }
    else if (typeof list.thumbnails === "string") {
      thumbs = this.normalizeThumbs(list.thumbnails.split(","));
    }
    else if (list.thumbnail) {
      thumbs = this.normalizeThumbs([list.thumbnail]);
    }
    else if (list.thumb) {
      thumbs = this.normalizeThumbs([list.thumb]);
    }
    else {
      const directThumb = this.getImageUrlFromValue(list);
      thumbs = directThumb ? [directThumb] : this.getThumbsFromListItems(list);
    }

    return thumbs
      .filter(function(thumb) { return thumb && String(thumb).trim().length > 0; })
      .slice(0, 5);
  }

  normalizeThumbs(values: Array<any>): Array<string> {
    const thumbs = [];
    if (!(values instanceof Array)) {
      return thumbs;
    }

    for (let i = 0; i < values.length; i++) {
      const thumb = this.getImageUrlFromValue(values[i]);
      if (thumb) {
        thumbs.push(thumb);
      }
      if (thumbs.length >= 5) {
        return thumbs;
      }
    }
    return thumbs;
  }

  getThumbsFromListItems(list: any): Array<string> {
    const thumbs = [];
    const itemArrays = [
      list.itemData,
      list.items,
      list.itemList,
      list.data && list.data.itemData,
      list.data && list.data.items,
      list.results && list.results.itemData,
      list.results && list.results.items
    ];

    for (let i = 0; i < itemArrays.length; i++) {
      const items = itemArrays[i];
      if (items instanceof Array) {
        for (let j = 0; j < items.length; j++) {
          const thumb = this.getImageUrlFromValue(items[j]);
          if (thumb) {
            thumbs.push(thumb);
          }
          if (thumbs.length >= 5) {
            return thumbs;
          }
        }
      }
    }

    return thumbs;
  }

  getImageUrlFromValue(value: any): string {
    if (!value) {
      return "";
    }

    if (typeof value === "string") {
      const parts = value.split(",");
      for (let i = parts.length - 1; i >= 0; i--) {
        const image = String(parts[i] || "").trim();
        if (this.isUsableImageUrl(image)) {
          return image;
        }
      }
      return "";
    }

    if (value instanceof Array) {
      for (let i = 0; i < value.length; i++) {
        const image = this.getImageUrlFromValue(value[i]);
        if (image) {
          return image;
        }
      }
      return "";
    }

    if (typeof value !== "object") {
      return "";
    }

    const directKeys = [
      "image",
      "imageURL",
      "imageUrl",
      "imageLargeUrl",
      "imageMediumUrl",
      "imageSmallUrl",
      "poster",
      "posterURL",
      "posterUrl",
      "thumbnail",
      "thumb",
      "itemImageUrl",
      "itemImageURL",
      "itemImage",
      "itemArtworkUrl",
      "itemArtworkURL",
      "itemArtwork",
      "artworkUrl600",
      "artworkUrl512",
      "artworkUrl100",
      "artworkUrl60",
      "artworkUrl",
      "cover",
      "coverURL",
      "coverUrl",
      "coverImage",
      "coverImageURL",
      "coverImageUrl",
      "url",
      "src",
      "large",
      "medium",
      "small",
      "full",
      "original"
    ];

    for (let i = 0; i < directKeys.length; i++) {
      const image = this.getImageUrlFromValue(value[directKeys[i]]);
      if (image) {
        return image;
      }
    }

    const containerKeys = ["images", "imageUrls", "imageURLs", "posters", "thumbnails", "covers", "artworks"];
    for (let j = 0; j < containerKeys.length; j++) {
      const image = this.getImageUrlFromValue(value[containerKeys[j]]);
      if (image) {
        return image;
      }
    }

    return "";
  }

  isUsableImageUrl(value: string): boolean {
    if (!value) {
      return false;
    }

    const image = value.trim();
    return image.indexOf("http://") === 0 ||
      image.indexOf("https://") === 0 ||
      image.indexOf("./assets/") === 0 ||
      image.indexOf("/assets/") === 0 ||
      /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(image);
  }

  getThumbStripClass(): string {
    const mediaType = Global.getMediaTypeForWishlistRequest();
    if (mediaType === Global.MUSIC || mediaType === Global.APPS || mediaType === Global.MACAPPS) {
      return "thumb-strip-square";
    }
    return "thumb-strip-poster";
  }

  showImagePlaceholder(event: Event) {
    if (!event || !event.target) {
      return;
    }
    const image = <HTMLImageElement>event.target;
    const placeholder = image.className && image.className.indexOf("author-image") >= 0 ?
      "./assets/images/profile_placeholder.png" :
      "./assets/images/item_placeholder.png";

    if (image.src && image.src.indexOf(placeholder.replace("./", "")) >= 0) {
      return;
    }

    image.src = placeholder;
  }

  storeChanged() {
    this.initMediaTypes();
    this.reload(true);
  }

  mediaChanged(mediaType) {
    this.selectedMediaType = Global.getMediaTypeForSelector(mediaType);

    if (mediaType === "movies") {
      mediaType = Global.BUYMOVIES;
    }
    else if (mediaType === "music") {
      mediaType = Global.ALBUMS;
    }

    StorageHelper.getInstance().save(StorageHelper.keys.mediaType, mediaType);
    this.reload(true);
  }

  reload(newUrl: boolean = false, replaceUrl?: boolean) {
    const targetUrl = this.getListSectionUrl(this.activeListSection);
    const url = targetUrl.substr(1);

    if (newUrl && this.router.url !== targetUrl) {
      if (replaceUrl === true) {
        this.router.navigate([url], { replaceUrl: true }).then(_ => this.loadAllLists());
      }
      else {
        this.router.navigate([url]).then(_ => this.loadAllLists());
      }
    }
    else {
      this.loadAllLists();
    }
  }

}
